import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { RichTextEditor } from '../components/ui/rich-text-editor';
import InstallPWA from '../components/InstallPWA';
import { authenticatedFetch } from '../utils/api';
import { renderFormattedLetter, convertLetterToFormattedHtml } from '../utils/letterFormatting';
import { queueOfflineUpload, NetworkService } from '../services/NetworkService';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Upload, 
  Folder, 
  FileText,
  Calendar,
  TrendingUp,
  School,
  Loader2,
  Plus,
  Link as LinkIcon,
  Eye,
  Trash2,
  BookOpen,
  Users,
  BarChart3,
  ArrowLeft,
  ArrowRight,
  Download,
  Send,
  CreditCard,
  CheckCircle,
  AlertCircle,
  LogOut,
  Clock,
  Bell,
  ExternalLink,
  UserPlus,
  Copy,
  ChevronDown,
  ChevronRight,
  Paperclip,
  FileIcon,
  FolderOpen,
  RefreshCw,
  MessageSquare,
  FileEdit,
  Sparkles,
  Printer,
  XCircle,
  X,
  GraduationCap
} from 'lucide-react';
import PromotionPreparation from '../components/PromotionPreparation';
import { Textarea } from '../components/ui/textarea';

// Form validation schemas
const connectTokenSchema = z.object({
  connect_token: z.string().min(1, "Please enter a connect token")
});

const lessonNoteSchema = z.object({
  folder_id: z.string().min(1, "Please select a folder"),
  week: z.number().min(1).max(18, "Week must be between 1 and 18"),
  subject: z.string().min(1, "Please enter subject"),
  class_level: z.string().min(1, "Please enter class level"),
  file_url: z.string().min(1, "Please provide file URL")
});

const TeacherDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [lessonNotes, setLessonNotes] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [adminLessonNotes, setAdminLessonNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isConnectDialogOpen, setIsConnectDialogOpen] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isSubscribeDialogOpen, setIsSubscribeDialogOpen] = useState(false);
  // Removed old dialog states - now using individual lesson note cards
  const [selectedClassesSubjects, setSelectedClassesSubjects] = useState([]);
  const [subscriptionPrice, setSubscriptionPrice] = useState(0);
  const [standardPrice, setStandardPrice] = useState(0);
  const [selectedWeek, setSelectedWeek] = useState('');
  const [referralLink, setReferralLink] = useState('');
  // State to track which week folders are expanded (collapsed by default)
  const [expandedWeeks, setExpandedWeeks] = useState({});
  // State for expanded weeks in uploaded notes section
  const [expandedUploadedWeeks, setExpandedUploadedWeeks] = useState({});
  // State for expanded headteacher comments
  const [expandedComments, setExpandedComments] = useState({});
  // Work Output state
  const [workOutputData, setWorkOutputData] = useState({});
  const [workOutputVerification, setWorkOutputVerification] = useState({});
  const [isWorkOutputDialogOpen, setIsWorkOutputDialogOpen] = useState(false);
  const [savingWorkOutput, setSavingWorkOutput] = useState(false);
  
  // Applications state
  const [applicationPlainText, setApplicationPlainText] = useState('');
  const [applicationType, setApplicationType] = useState('');
  const [applicationRecipient, setApplicationRecipient] = useState('headteacher');
  const [generatedLetter, setGeneratedLetter] = useState('');
  
  // Rich Text Editor state for application editing
  const [showRichEditor, setShowRichEditor] = useState(false);
  const [editedLetterHtml, setEditedLetterHtml] = useState('');
  const [isEditingLetter, setIsEditingLetter] = useState(false);
  const [generatingLetter, setGeneratingLetter] = useState(false);
  const [submittingApplication, setSubmittingApplication] = useState(false);
  const [showLetterPreview, setShowLetterPreview] = useState(false);
  const [myApplications, setMyApplications] = useState([]);
  const [applicationSubTab, setApplicationSubTab] = useState('create'); // 'create' or 'submitted'
  const [selectedApplication, setSelectedApplication] = useState(null); // For viewing full application
  const [showApplicationModal, setShowApplicationModal] = useState(false);

  // Tab control for promotion payment redirect
  const [mainTab, setMainTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'folders';
  });

  // Note Editor state
  const [editingNote, setEditingNote] = useState(null); // { id, title, content_html }
  const [editNoteContent, setEditNoteContent] = useState('');
  const [loadingNoteEdit, setLoadingNoteEdit] = useState(false);
  const [savingNoteEdit, setSavingNoteEdit] = useState(false);


  
  // Profile/Settings state
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [uploadingProfilePic, setUploadingProfilePic] = useState(false);
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const [profileFormData, setProfileFormData] = useState({
    town: '',
    phone: '',
    file_number: '',
    mobile_money_number: '',
    mobile_money_network: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Lesson Delivery Status state
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [selectedDeliveryNote, setSelectedDeliveryNote] = useState(null);
  const [deliveryStatus, setDeliveryStatus] = useState('');
  const [deliveryRemarks, setDeliveryRemarks] = useState('');
  const [deliveryActionPlan, setDeliveryActionPlan] = useState('');
  const [savingDeliveryStatus, setSavingDeliveryStatus] = useState(false);
  
  // Respond to Comment state
  const [showRespondModal, setShowRespondModal] = useState(false);
  const [selectedCommentNote, setSelectedCommentNote] = useState(null);
  const [responseNote, setResponseNote] = useState('');
  const [submittingResponse, setSubmittingResponse] = useState(false);
  
  // User Notifications state (for payment links, etc.)
  const [userNotifications, setUserNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  
  // Application draft state
  const [letterSaved, setLetterSaved] = useState(false);
  const [savingLetter, setSavingLetter] = useState(false);
  const [applicationDraftId, setApplicationDraftId] = useState(null);

  // Application Attachments state
  const [applicationAttachments, setApplicationAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const attachmentInputRef = React.useRef(null);
  
  // Application types
  const applicationTypes = [
    { value: 'permission_for_absent', label: 'Permission for Absent' },
    { value: 'application_for_reposting', label: 'Application for Reposting' },
    { value: 'application_for_leave', label: 'Application for Leave' },
    { value: 'application_for_return_movement', label: 'Application for Return Movement' },
    { value: 'application_for_promotion', label: 'Application for Promotion' },
    { value: 'application_for_upgrading', label: 'Application for Upgrading' },
    { value: 'application_for_salary_adjustment', label: 'Application for Salary Adjustment' }
  ];
  
  // Utility function to format letter for display (strips technical markers)
  const formatLetterForDisplay = (letter) => {
    if (!letter) return '';
    
    // Remove alignment markers but keep the content
    let formatted = letter
      .replace(/\[RIGHT\]\s*/g, '')
      .replace(/\[LEFT\]\s*/g, '')
      .replace(/\[CENTER\]\s*/g, '')
      .replace(/\[BOLD_UNDERLINE\]/g, '')
      .replace(/\[\/BOLD_UNDERLINE\]/g, '')
      .replace(/\[\/?BOLD_UNDERLINE\]/g, '')
      .replace(/\[SIGNATURE_PLACEHOLDER\]/g, '\n\n____________________\n(Signature)')
      .replace(/\[STAMP_PLACEHOLDER\]/g, '\n\n[Official Stamp]')
      .replace(/\[SIGNATURE\]/g, '\n\n____________________\n(Signature)')
      .replace(/\[STAMP\]/g, '\n\n[Official Stamp]')
      .replace(/```/g, ''); // Remove any code block markers
    
    return formatted.trim();
  };

  // Note: renderFormattedLetter and convertLetterToFormattedHtml are imported from ../utils/letterFormatting
  
  // Offline/Cache state
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [dataFromCache, setDataFromCache] = useState(false);
  
  // Authentication
  const { logout, user: authUser } = useAuth();

  // Class and subject definitions
  const kgPrimaryClasses = [
    'KG 1', 'KG 2', 'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5', 'Class 6'
  ];
  
  const jhsSubjects = [
    'Mathematics', 'English Language', 'Integrated Science', 'Social Studies', 'Religious and Moral Education', 
    'Computing', 'Creative Arts', 'Ghanaian Language', 'French', 'Career Technology'
  ];

  // KG/Primary subjects for work output
  const kgPrimarySubjects = [
    'English Language', 'Mathematics', 'Creative Arts', 'Our World Our People', 
    'Religious and Moral Education', 'Ghanaian Language', 'Physical Education',
    'Computing', 'French'
  ];

  // Weeks for work output (1-15)
  const workOutputWeeks = Array.from({ length: 15 }, (_, i) => i + 1);

  // JHS classes for work output grouping
  const jhsClasses = ['JHS 1', 'JHS 2', 'JHS 3'];

  // State for expanded JHS classes in work output
  const [expandedWorkOutputClasses, setExpandedWorkOutputClasses] = useState({'JHS 1': true, 'JHS 2': false, 'JHS 3': false});

  // Check if teacher is JHS based on subscription
  const isJhsTeacher = () => {
    if (!subscription || !subscription.classes_subjects) return false;
    const subscribedItems = subscription.classes_subjects;
    if (subscribedItems.length === 0) return false;
    
    const firstItem = subscribedItems[0];
    // If it's a JHS subject string
    if (typeof firstItem === 'string' && jhsSubjects.includes(firstItem)) {
      return true;
    }
    // If it's an object with subject key (JHS format)
    if (typeof firstItem === 'object' && firstItem.subject) {
      return true;
    }
    return false;
  };

  // Get subjects for work output based on subscription
  const getWorkOutputSubjects = () => {
    if (!subscription || !subscription.classes_subjects) return [];
    
    const subscribedItems = subscription.classes_subjects;
    
    // Check if it's JHS (subject-based) or KG/Primary (class-based)
    if (subscribedItems.length > 0) {
      const firstItem = subscribedItems[0];
      // If it's a JHS subject
      if (typeof firstItem === 'string' && jhsSubjects.includes(firstItem)) {
        return subscribedItems;
      }
      // If it's an object with subject key (JHS format)
      if (typeof firstItem === 'object' && firstItem.subject) {
        return subscribedItems.map(item => item.subject);
      }
      // KG/Primary class - return all subjects for that level
      return kgPrimarySubjects;
    }
    return [];
  };

  const {
    register: registerConnect,
    handleSubmit: handleSubmitConnect,
    reset: resetConnect,
    formState: { errors: errorsConnect }
  } = useForm({
    resolver: zodResolver(connectTokenSchema)
  });

  const {
    register: registerUpload,
    handleSubmit: handleSubmitUpload,
    reset: resetUpload,
    formState: { errors: errorsUpload }
  } = useForm({
    resolver: zodResolver(lessonNoteSchema)
  });

  // Load dashboard data
  const loadDashboard = async () => {
    setLoading(true);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await authenticatedFetch(`${backendUrl}/api/teacher/dashboard`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
        setLessonNotes(data.lesson_notes || []);
        setDataFromCache(false);
        setIsOffline(false);
        
        // Generate referral link based on user email
        if (data.user && data.user.email) {
          const referralCode = btoa(data.user.email).substring(0, 12);
          setReferralLink(`${window.location.origin}/register?ref=${referralCode}`);
        }
        
        // Load subscription data
        await loadSubscription();
        
        // ALWAYS load admin lesson notes - the endpoint will check subscription internally
        await loadAdminLessonNotes();
        
        // Load work output data
        await loadWorkOutput();
        
        // Load teacher's applications
        await loadMyApplications();
      } else {
        throw new Error('Failed to load dashboard');
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
      setIsOffline(!navigator.onLine);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  // Load subscription details
  const loadSubscription = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await authenticatedFetch(`${backendUrl}/api/teacher/subscription`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
    }
  };

  // Load admin lesson notes for subscribed classes/subjects
  const loadAdminLessonNotes = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      console.log('🔍 Fetching admin lesson notes from:', `${backendUrl}/api/teacher/admin-lesson-notes`);
      
      const response = await authenticatedFetch(`${backendUrl}/api/teacher/admin-lesson-notes`, {
        credentials: 'include'
      });

      console.log('📡 Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('📦 Received data:', data);
        console.log('📦 Data type:', typeof data, 'Is array:', Array.isArray(data));
        
        // Handle new data structure with lesson_notes array
        if (data && data.lesson_notes) {
          console.log('✅ Setting admin lesson notes:', data.lesson_notes.length, 'notes');
          setAdminLessonNotes(data.lesson_notes);
        } else {
          // Fallback for old structure or direct array
          console.log('⚠️ Using fallback structure');
          setAdminLessonNotes(Array.isArray(data) ? data : []);
        }
      } else {
        console.error('❌ Response not ok:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);
      }
    } catch (error) {
      console.error('❌ Error loading admin lesson notes:', error);
    }
  };

  // Load work output data
  const loadWorkOutput = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/teacher/work-output`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data && data.work_output) {
          setWorkOutputData(data.work_output);
        }
        if (data && data.verification) {
          setWorkOutputVerification(data.verification);
        }
      }
    } catch (error) {
      console.error('Error loading work output:', error);
    }
  };

  // Update work output for a subject/week
  const updateWorkOutput = (subject, week, value, jhsClass = null) => {
    setWorkOutputData(prev => {
      const updated = { ...prev };
      // For JHS teachers, use "JHS 1|Mathematics" format as key
      const key = jhsClass ? `${jhsClass}|${subject}` : subject;
      if (!updated[key]) {
        updated[key] = {};
      }
      updated[key][week] = value === '' ? '' : parseInt(value) || 0;
      return updated;
    });
  };

  // Calculate total for a subject (handles both formats)
  const calculateSubjectTotal = (subject, jhsClass = null) => {
    const key = jhsClass ? `${jhsClass}|${subject}` : subject;
    if (!workOutputData[key]) return 0;
    return workOutputWeeks.reduce((sum, week) => {
      const value = workOutputData[key][week];
      return sum + (parseInt(value) || 0);
    }, 0);
  };

  // Get verification status (handles both formats)
  const getVerificationStatus = (subject, week, jhsClass = null) => {
    const key = jhsClass ? `${jhsClass}|${subject}` : subject;
    return workOutputVerification[key]?.[week];
  };

  // Submit work output to headteacher
  const saveWorkOutput = async () => {
    setSavingWorkOutput(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/teacher/submit-work-output`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ work_output: workOutputData })
      });

      if (response.ok) {
        toast.success('Work output submitted to headteacher!');
        // Reload to get latest verification status
        await loadWorkOutput();
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      console.error('Error submitting work output:', error);
      toast.error('Failed to submit work output. Please try again.');
    } finally {
      setSavingWorkOutput(false);
    }
  };

  // Generate AI-formatted application letter
  const generateApplicationLetter = async () => {
    if (!applicationType) {
      toast.error('Please select an application type');
      return;
    }
    if (!applicationPlainText.trim()) {
      toast.error('Please enter your application content');
      return;
    }
    
    setGeneratingLetter(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/teacher/generate-application-letter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          application_type: applicationTypes.find(t => t.value === applicationType)?.label || applicationType,
          plain_text: applicationPlainText,
          recipient: applicationRecipient
        })
      });

      if (response.ok) {
        const data = await response.json();
        setGeneratedLetter(data.formatted_letter);
        // Convert structured text to HTML for rich text editor
        const htmlContent = convertStructuredTextToHtml(data.formatted_letter);
        setEditedLetterHtml(htmlContent);
        setShowLetterPreview(true);
        toast.success('Letter generated — edit below, then save.');
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate letter');
      }
    } catch (error) {
      console.error('Error generating letter:', error);
      toast.error(error.message || 'Failed to generate letter. Please try again.');
    } finally {
      setGeneratingLetter(false);
    }
  };

  // Convert structured text (with tags like [CENTER], [BOLD]) to HTML
  const convertStructuredTextToHtml = (text) => {
    if (!text) return '';
    
    // First, process multi-line formatting tags on the FULL text (before splitting)
    let fullText = text
      // Handle BOLD_UNDERLINE across paragraphs (must be before paragraph split)
      .replace(/\[BOLD_UNDERLINE\]([\s\S]*?)\[\/BOLD_UNDERLINE\]/g, '<strong><u>$1</u></strong>')
      // Handle CENTER across paragraphs
      .replace(/\[CENTER\]([\s\S]*?)\[\/CENTER\]/g, '<center_block>$1</center_block>')
      // Handle RIGHT_ALIGN across paragraphs
      .replace(/\[RIGHT_ALIGN\]([\s\S]*?)\[\/RIGHT_ALIGN\]/g, '<right_block>$1</right_block>')
      .replace(/\[RIGHT\]([\s\S]*?)\[\/RIGHT\]/g, '<right_block>$1</right_block>')
      // Signature/stamp markers: emit the same HTML pattern the backend's
      // _embed_signature_server_side already knows how to swap for the real
      // image at PDF-generation time — keeps this local converter in sync
      // with the shared one instead of leaking the raw [SIGNATURE_PLACEHOLDER]
      // token as visible text.
      .replace(/\[SIGNATURE_PLACEHOLDER\]/gi, '<right_block><em>(Signature)</em></right_block>')
      .replace(/\[SIGNATURE\]/gi, '<right_block><em>(Signature)</em></right_block>')
      .replace(/\[STAMP_PLACEHOLDER\]/gi, '<right_block><em>(Stamp)</em></right_block>')
      .replace(/\[STAMP\]/gi, '<right_block><em>(Stamp)</em></right_block>')
      // Strip any leftover unmatched formatting tags
      .replace(/\[\/?BOLD_UNDERLINE\]/g, '')
      .replace(/\[\/?RIGHT\]/g, '')
      .replace(/\[\/?RIGHT_ALIGN\]/g, '')
      .replace(/\[\/?CENTER\]/g, '')
      .replace(/\[\/?BOLD\]/g, '')
      .replace(/\[\/?UNDERLINE\]/g, '');

    let html = fullText
      .split('\n\n')
      .map(para => {
        let processed = para
          // Handle inline BOLD tags
          .replace(/\[BOLD\](.*?)\[\/BOLD\]/g, '<strong>$1</strong>')
          // Handle inline UNDERLINE tags
          .replace(/\[UNDERLINE\](.*?)\[\/UNDERLINE\]/g, '<u>$1</u>')
          // Convert center/right block markers to styled paragraphs
          .replace(/<center_block>([\s\S]*?)<\/center_block>/g, '<p style="text-align: center;">$1</p>')
          .replace(/<right_block>([\s\S]*?)<\/right_block>/g, '<p style="text-align: right;">$1</p>');
        
        // Wrap in paragraph if not already wrapped
        if (!processed.startsWith('<p')) {
          processed = `<p>${processed}</p>`;
        }
        return processed;
      })
      .join('');
    
    // Handle single line breaks within paragraphs
    html = html.replace(/\n/g, '<br/>');
    
    return html;
  };

  // Convert HTML back to structured text for PDF generation
  const convertHtmlToStructuredText = (html) => {
    if (!html) return '';
    
    // Create a temporary DOM element to parse HTML
    const div = document.createElement('div');
    div.innerHTML = html;
    
    let text = '';
    
    const processNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent;
      }
      
      if (node.nodeType === Node.ELEMENT_NODE) {
        const tag = node.tagName.toLowerCase();
        let content = Array.from(node.childNodes).map(processNode).join('');
        
        // Handle text alignment
        const style = node.getAttribute('style') || '';
        if (style.includes('text-align: center')) {
          content = `[CENTER]${content}[/CENTER]`;
        } else if (style.includes('text-align: right')) {
          content = `[RIGHT_ALIGN]${content}[/RIGHT_ALIGN]`;
        }
        
        // Handle formatting
        if (tag === 'strong' || tag === 'b') {
          content = `[BOLD]${content}[/BOLD]`;
        }
        if (tag === 'u') {
          content = `[UNDERLINE]${content}[/UNDERLINE]`;
        }
        if (tag === 'p') {
          content = content + '\n\n';
        }
        if (tag === 'br') {
          content = '\n';
        }
        
        return content;
      }
      
      return '';
    };
    
    text = Array.from(div.childNodes).map(processNode).join('');
    
    // Clean up extra whitespace
    text = text.replace(/\n{3,}/g, '\n\n').trim();
    
    return text;
  };

  // Open rich text editor for editing
  const openRichEditor = () => {
    setIsEditingLetter(true);
    setShowRichEditor(true);
  };

  // Close rich text editor and save changes
  const closeRichEditor = () => {
    setShowRichEditor(false);
    setIsEditingLetter(false);
  };

  // Done editing - convert back to structured text and prepare for submission
  const doneEditingLetter = () => {
    const structuredText = convertHtmlToStructuredText(editedLetterHtml);
    setGeneratedLetter(structuredText);
    setShowRichEditor(false);
    setIsEditingLetter(false);
    toast.success('Letter updated! Ready to submit.');
  };

  // Handle attachment upload
  const handleAttachmentUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    setUploadingAttachment(true);
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    
    for (const file of files) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Maximum size is 5MB`);
        continue;
      }
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await authenticatedFetch(`${backendUrl}/api/application/upload-attachment`, {
          method: 'POST',
          credentials: 'include',
          body: formData
        });
        
        if (response.ok) {
          const data = await response.json();
          setApplicationAttachments(prev => [...prev, {
            id: data.attachment_id,
            filename: data.filename,
            file_size: data.file_size,
            content_type: data.content_type
          }]);
          toast.success(`Attached: ${file.name}`);
        } else {
          const error = await response.json();
          throw new Error(error.detail || 'Failed to upload attachment');
        }
      } catch (error) {
        console.error('Error uploading attachment:', error);
        toast.error(`Failed to attach ${file.name}: ${error.message}`);
      }
    }
    
    setUploadingAttachment(false);
    // Clear the file input
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = '';
    }
  };

  // Remove attachment
  const removeAttachment = async (attachmentId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/application/attachment/${attachmentId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        setApplicationAttachments(prev => prev.filter(a => a.id !== attachmentId));
        toast.success('Attachment removed');
      }
    } catch (error) {
      console.error('Error removing attachment:', error);
      toast.error('Failed to remove attachment');
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Submit application to headteacher
  const submitApplication = async () => {
    if (!applicationDraftId) {
      toast.error('Please save the letter first');
      return;
    }
    
    setSubmittingApplication(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await authenticatedFetch(`${backendUrl}/api/teacher/submit-application`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          draft_id: applicationDraftId,
          attachment_ids: applicationAttachments.map(a => a.id)
        })
      });

      if (response.ok) {
        toast.success('Application submitted to headteacher successfully!');
        // Reset form
        setApplicationPlainText('');
        setApplicationType('');
        setGeneratedLetter('');
        setEditedLetterHtml('');
        setShowLetterPreview(false);
        setShowRichEditor(false);
        setIsEditingLetter(false);
        setLetterSaved(false);
        setApplicationDraftId(null);
        setApplicationAttachments([]);
        loadMyApplications();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error(error.message || 'Failed to submit application. Please try again.');
    } finally {
      setSubmittingApplication(false);
    }
  };

  // Load teacher's submitted applications
  const loadMyApplications = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/teacher/applications`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setMyApplications(data.applications || []);
      }
    } catch (error) {
      console.error('Error loading applications:', error);
    }
  };

  // Print/Download letter as PDF
  const printLetter = () => {
    // Use the edited Tiptap HTML if available, falling back to generated letter
    const htmlToPrint = editedLetterHtml || '';
    if (!htmlToPrint && !generatedLetter) {
      toast.error('No letter content available to print');
      return;
    }
    
    let printContent = htmlToPrint;
    if (!printContent) {
      // Fallback: convert structured text to HTML
      printContent = convertStructuredTextToHtml(generatedLetter);
    }
    
    // Embed signature if not already embedded (check for data: URI or backend URL)
    const signatureUrl = userProfile?.digital_signature;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    if (signatureUrl && !printContent.includes('alt="Signature"')) {
      const fullSigUrl = `${backendUrl}${signatureUrl}`;
      const sigImgTag = `<div style="margin: 10px 0;"><img src="${fullSigUrl}" alt="Signature" style="max-height: 70px; max-width: 200px;" /></div>`;
      // Replace first signature placeholder only, remove the rest
      let inserted = false;
      const patterns = [
        /_{3,}[\s\S]*?\(Signature\)/gi,
        /\[SIGNATURE_PLACEHOLDER\]/gi,
        /\[SIGNATURE\]/gi,
        /<em>\(Signature\)<\/em>/gi,
        /\(Signature\)/gi,
      ];
      for (const pat of patterns) {
        if (pat.test(printContent)) {
          if (!inserted) {
            printContent = printContent.replace(pat, sigImgTag);
            inserted = true;
          }
          printContent = printContent.replace(pat, '');
        }
      }
    }
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Application Letter</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Times New Roman', Times, serif;
            font-size: 14pt;
            line-height: 1.6;
            max-width: 8.5in;
            margin: 0 auto;
            padding: 0.5in 0.75in;
          }
          h1, h2, h3 { font-size: 16pt; margin: 0.5em 0; }
          p { margin: 0 0 0.6em 0; }
          strong { font-weight: bold; }
          u { text-decoration: underline; }
          img { max-width: 200px; max-height: 80px; }
          @media print {
            body { margin: 0; padding: 0.4in 0.6in; }
            @page { margin: 0.4in; size: A4; }
          }
        </style>
      </head>
      <body>${printContent}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Print selected application letter from modal
  const printSelectedApplication = () => {
    // Prefer html_content (Tiptap HTML) if available
    const htmlContent = selectedApplication?.html_content;
    const formattedLetter = selectedApplication?.formatted_letter;
    if (!htmlContent && !formattedLetter) {
      toast.error('No letter content available to print');
      return;
    }
    
    let printContent = htmlContent || convertStructuredTextToHtml(formattedLetter);
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${selectedApplication.application_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} - Application Letter</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Times New Roman', Times, serif;
            font-size: 14pt;
            line-height: 1.6;
            max-width: 8.5in;
            margin: 0 auto;
            padding: 0.5in 0.75in;
          }
          h1, h2, h3 { font-size: 16pt; margin: 0.5em 0; }
          p { margin: 0 0 0.6em 0; }
          strong { font-weight: bold; }
          u { text-decoration: underline; }
          img { max-width: 200px; max-height: 80px; }
          @media print {
            body { margin: 0; padding: 0.4in 0.6in; }
            @page { margin: 0.4in; size: A4; }
          }
        </style>
      </head>
      <body>${printContent}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Load user notifications (for payment links, etc.)
  const loadUserNotifications = async () => {
    setLoadingNotifications(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/user/notifications`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserNotifications(data.notifications || []);
        setUnreadNotificationCount(data.notifications?.filter(n => !n.read)?.length || 0);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  // Mark notification as read
  const markNotificationRead = async (notificationId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await authenticatedFetch(`${backendUrl}/api/user/notifications/${notificationId}/read`, {
        method: 'PUT',
        credentials: 'include'
      });
      
      setUserNotifications(prev => 
        prev.map(n => n.id === notificationId ? {...n, read: true} : n)
      );
      setUnreadNotificationCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification read:', error);
    }
  };

  // Mark all unread notifications as read when panel opens
  const markAllNotificationsRead = async () => {
    const unread = userNotifications.filter(n => !n.read);
    if (unread.length === 0) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    for (const n of unread) {
      try {
        await authenticatedFetch(`${backendUrl}/api/user/notifications/${n.id}/read`, {
          method: 'PUT', credentials: 'include'
        });
      } catch (e) { /* silent */ }
    }
    setUserNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadNotificationCount(0);
  };

  // Handle payment link click
  const handlePaymentClick = (notification) => {
    if (notification.action_url) {
      // Mark as read first
      if (!notification.read) {
        markNotificationRead(notification.id);
      }
      // Open payment link in new tab
      window.open(notification.action_url, '_blank');
    }
  };

  // Load user profile
  const loadUserProfile = async () => {
    setProfileLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/user/profile`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setUserProfile(data.profile);
        setProfileFormData({
          town: data.profile.town || '',
          phone: data.profile.phone || '',
          file_number: data.profile.file_number || '',
          mobile_money_number: data.profile.mobile_money_number || '',
          mobile_money_network: data.profile.mobile_money_network || '',
          school_name: data.profile.school_name || '',
          district_name: data.profile.district_name || '',
          region_name: data.profile.region_name || '',
          regional_file_number: data.profile.regional_file_number || '',
          staff_id: data.profile.staff_id || ''
        });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setProfileLoading(false);
    }
  };

  // Upload profile picture
  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingProfilePic(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const formData = new FormData();
      formData.append('file', file);

      const response = await authenticatedFetch(`${backendUrl}/api/user/upload-profile-picture`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Profile picture uploaded successfully!');
        // Refresh profile
        loadUserProfile();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload profile picture');
      }
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      toast.error(error.message || 'Failed to upload profile picture');
    } finally {
      setUploadingProfilePic(false);
    }
  };

  // Upload digital signature
  const handleSignatureUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingSignature(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const formData = new FormData();
      formData.append('file', file);

      const response = await authenticatedFetch(`${backendUrl}/api/user/upload-signature`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Digital signature uploaded successfully!');
        loadUserProfile();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload signature');
      }
    } catch (error) {
      console.error('Error uploading signature:', error);
      toast.error(error.message || 'Failed to upload signature');
    } finally {
      setUploadingSignature(false);
    }
  };

  // Save profile details
  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/user/update-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileFormData),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Profile updated successfully!');
        loadUserProfile();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(error.message || 'Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  // Delete upload
  const handleDeleteUpload = async (uploadType) => {
    if (!window.confirm(`Are you sure you want to delete your ${uploadType.replace('_', ' ')}?`)) {
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/user/delete-upload/${uploadType}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success(`${uploadType.replace('_', ' ')} deleted successfully!`);
        loadUserProfile();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete');
      }
    } catch (error) {
      console.error('Error deleting upload:', error);
      toast.error(error.message || 'Failed to delete');
    }
  };

  // Load pricing
  const loadPricing = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await authenticatedFetch(`${backendUrl}/api/teacher/pricing`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setStandardPrice(data.lesson_subscription_price || 50);
      }
    } catch (error) {
      console.error('Error loading pricing:', error);
      setStandardPrice(50); // Default fallback
    }
  };

  // Handle class/subject selection
  const handleSelectionChange = (item, checked) => {
    console.log("🔄 Selection change triggered");
    console.log("Item:", item);
    console.log("Checked:", checked);
    console.log("Current selection before change:", selectedClassesSubjects);
    
    let newSelection;
    
    if (checked) {
      newSelection = [...selectedClassesSubjects, item];
      console.log("✅ Adding item to selection");
    } else {
      newSelection = selectedClassesSubjects.filter(selected => selected !== item);
      console.log("❌ Removing item from selection");
    }
    
    console.log("New selection after change:", newSelection);
    setSelectedClassesSubjects(newSelection);
    
    console.log("📊 Calculating price for new selection");
    calculatePrice(newSelection);
    
    // Add a small delay to see if state updates properly
    setTimeout(() => {
      console.log("🔍 Selection state after update:", selectedClassesSubjects);
    }, 100);
  };

  // Calculate subscription price based on selection
  const calculatePrice = (selection) => {
    if (selection.length === 0) {
      setSubscriptionPrice(0);
      return;
    }

    // Check if selection is KG/Primary classes or JHS subjects
    const isKgPrimary = selection.some(item => kgPrimaryClasses.includes(item));
    const isJhs = selection.some(item => jhsSubjects.includes(item));

    let price = standardPrice;

    if (isKgPrimary) {
      // KG/Primary logic: 1 class = standard price, 2+ classes = double price per additional class
      if (selection.length === 1) {
        price = standardPrice;
      } else {
        price = standardPrice * selection.length; // Double for each additional class
      }
    } else if (isJhs) {
      // JHS logic: 2 subjects = standard price
      if (selection.length <= 2) {
        price = standardPrice;
      } else {
        // Additional subjects: +1 = +50%, +2 = +100% (double), etc.
        const additionalSubjects = selection.length - 2;
        price = standardPrice + (standardPrice * 0.5 * additionalSubjects);
      }
    }

    setSubscriptionPrice(price);
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      // Call logout function from AuthContext first
      await logout();
      
      // Then redirect to homepage
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout even if error occurs
      localStorage.removeItem('auth_token');
      window.location.href = '/';
    }
  };

  // Process subscription
  const processSubscription = async () => {
    console.log("🔄 processSubscription called");
    console.log("Selected classes/subjects:", selectedClassesSubjects);
    console.log("Subscription price:", subscriptionPrice);
    
    if (selectedClassesSubjects.length === 0) {
      console.log("❌ No classes/subjects selected");
      toast.error("Please select at least one class or subject");
      return;
    }

    console.log("✅ Proceeding with subscription creation");
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      console.log("Backend URL:", backendUrl);
      
      // Create subscription
      const subscriptionData = {
        classes_subjects: selectedClassesSubjects,
        total_amount: subscriptionPrice
      };

      console.log("Sending subscription data:", subscriptionData);

      const response = await authenticatedFetch(`${backendUrl}/api/teacher/create-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(subscriptionData)
      });

      console.log("Subscription response status:", response.status);

      if (response.ok) {
        const result = await response.json();
        console.log("Subscription result:", result);
        
        // Check if user already has an active/pending subscription
        if (result.success === false && result.message) {
          console.log("⚠️ Subscription already exists:", result.message);
          toast.info(result.message);
          setIsSubscribeDialogOpen(false);
          // Reload subscription status
          loadSubscription();
          return;
        }
        
        // Check if we have a Paystack payment URL
        if (result.payment_url && result.payment_url.includes('paystack.com')) {
          console.log("✅ Redirecting to Paystack:", result.payment_url);
          toast.success("Redirecting to payment gateway...");
          // Redirect to actual Paystack payment
          window.location.href = result.payment_url;
        } else if (result.paystack_url && result.paystack_url.includes('paystack.com')) {
          console.log("✅ Redirecting to Paystack (alternative field):", result.paystack_url);
          toast.success("Redirecting to payment gateway...");
          // Alternative Paystack URL field
          window.location.href = result.paystack_url;
        } else {
          console.log("⚠️ No payment URL found in response:", result);
          // This shouldn't happen if Paystack is configured
          toast.error("Payment system error. Please try again or contact support.");
          setIsSubscribeDialogOpen(false);
        }
      } else {
        const errorData = await response.json();
        console.error("Subscription error response:", errorData);
        throw new Error(errorData.detail || 'Failed to create subscription');
      }
    } catch (error) {
      console.error('Error creating subscription:', error);
      toast.error(error.message || "Failed to process subscription");
    }
  };

  // Toggle week folder expand/collapse
  const toggleWeekFolder = (className, week) => {
    const key = `${className}-${week}`;
    setExpandedWeeks(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Check if a week folder is expanded
  const isWeekExpanded = (className, week) => {
    const key = `${className}-${week}`;
    return expandedWeeks[key] || false;
  };

  // Toggle week folder expand/collapse for uploaded notes
  const toggleUploadedWeekFolder = (folderName, week) => {
    const key = `uploaded-${folderName}-${week}`;
    setExpandedUploadedWeeks(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Check if an uploaded week folder is expanded
  const isUploadedWeekExpanded = (folderName, week) => {
    const key = `uploaded-${folderName}-${week}`;
    return expandedUploadedWeeks[key] || false;
  };

  // State for download links (mobile-safe approach)
  const [downloadLinks, setDownloadLinks] = useState({});
  const [generatingLink, setGeneratingLink] = useState({});
  const [viewingNote, setViewingNote] = useState(null);
  const [viewingNoteContent, setViewingNoteContent] = useState('');

  // Generate a signed download URL for mobile-safe downloads
  const generateDownloadLink = async (note) => {
    const noteId = note.id;
    setGeneratingLink(prev => ({ ...prev, [noteId]: true }));
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Create a temporary download token
      const tokenResponse = await authenticatedFetch(`${backendUrl}/api/teacher/create-download-token/${noteId}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!tokenResponse.ok) {
        throw new Error('Failed to generate download link');
      }
      
      const { token } = await tokenResponse.json();
      const downloadUrl = `${backendUrl}/api/public/download-pdf/${token}`;
      
      // Store the link for this note
      setDownloadLinks(prev => ({ ...prev, [noteId]: downloadUrl }));
      
      toast.success('📥 Download link ready! Tap the green button to open PDF.', { duration: 5000 });
      
    } catch (error) {
      console.error('Error generating download link:', error);
      toast.error('Failed to generate download link');
    } finally {
      setGeneratingLink(prev => ({ ...prev, [noteId]: false }));
    }
  };

  // View/preview a lesson note for review
  const previewLessonNote = async (note) => {
    setViewingNote(note);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/teacher/admin-lesson-notes/${note.id}/content`);
      if (response.ok) {
        const data = await response.json();
        setViewingNoteContent(data.html_content || data.content || 'No content available');
      } else {
        setViewingNoteContent(note.lesson_content || note.content || 'No content available');
      }
    } catch (e) {
      setViewingNoteContent(note.lesson_content || note.content || 'No content available');
    }
  };

  // Download individual lesson note - Unified Mobile-Safe Version
  // Uses real <a> tag click for both mobile and desktop to trigger native browser handling
  const downloadIndividualNote = async (note) => {
    const noteId = note.id;
    setGeneratingLink(prev => ({ ...prev, [noteId]: true }));
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      let fileName = note.file_name || `${note.class_name}-Week${note.week}-${note.subject || 'lesson'}-lesson_note.pdf`;
      if (!fileName.toLowerCase().endsWith('.pdf')) {
        fileName = `${fileName}.pdf`;
      }
      
      toast.info(`Preparing personalized PDF: ${fileName}`, { duration: 2000 });
      
      // Download personalized, watermarked PDF
      const response = await authenticatedFetch(`${backendUrl}/api/teacher/lesson-note-download/${noteId}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to download');
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      // Store the blob link for the "Open PDF" button
      setDownloadLinks(prev => ({ ...prev, [noteId]: url }));
      
      // Trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Downloaded: ${fileName}`, { duration: 3000 });
      
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error(`Download failed: ${error.message}`);
    } finally {
      setGeneratingLink(prev => ({ ...prev, [noteId]: false }));
    }
  };

  // Submit lesson note to headteacher - with offline resilience
  const submitToHeadteacher = async (note) => {
    try {
      console.log("Submitting to headteacher:", note);
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const requestId = `submit_${note.id}_${Date.now()}`;
      
      // All lesson notes from "My Lesson Notes" are individual PDFs stored in the system
      // Use internal server transfer for all of them (no file picker needed)
      console.log("📄 Internal server transfer - Direct submission");
      
      toast.info("📤 Submitting lesson note to headteacher...");
      
      const payload = {
        lesson_note_id: note.id,
        file_name: note.file_name,
        subject: note.subject || note.class_subject,
        class_name: note.class_name,
        week: note.week,
        term: note.term,
        education_level: note.education_level
      };
      
      // Check if online - if not, queue for later
      if (!NetworkService.isOnline()) {
        queueOfflineUpload({
          requestId,
          url: `${backendUrl}/api/teacher/submit-to-headteacher`,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify(payload),
          type: 'submission',
          metadata: { noteName: note.file_name }
        });
        toast.success("📥 Saved offline - will submit when connected");
        return;
      }
      
      // Online - try with resilient fetch
      try {
        const response = await NetworkService.resilientFetch(
          `${backendUrl}/api/teacher/submit-to-headteacher`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            },
            credentials: 'include',
            body: JSON.stringify(payload)
          },
          {
            maxRetries: 3,
            requestId,
            onRetry: (attempt, max) => {
              toast.info(`Retrying... (${attempt}/${max})`);
            }
          }
        );

        if (response.ok) {
          const result = await response.json();
          console.log("Submission result:", result);
          toast.success("✅ Lesson note submitted successfully to headteacher");
        } else {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to submit to headteacher');
        }
      } catch (fetchError) {
        // If fetch failed completely (e.g., went offline during request), queue it
        if (fetchError.message === 'OFFLINE' || !navigator.onLine) {
          queueOfflineUpload({
            requestId,
            url: `${backendUrl}/api/teacher/submit-to-headteacher`,
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            },
            body: JSON.stringify(payload),
            type: 'submission',
            metadata: { noteName: note.file_name }
          });
          toast.success("📥 Connection lost - saved offline");
        } else {
          throw fetchError;
        }
      }
      
    } catch (error) {
      console.error('Error submitting to headteacher:', error);
      toast.error(`❌ Failed to submit: ${error.message}`);
    }
  };

  // Legacy functions removed - now using individual lesson note card actions
  const onConnectFolder = async (data) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await authenticatedFetch(`${backendUrl}/api/teacher/connect-folder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data)
      });

      if (response.ok) {
        const result = await response.json();
        
        toast.success("Successfully connected to folder");

        setIsConnectDialogOpen(false);
        resetConnect();
        loadDashboard(); // Refresh dashboard
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to connect to folder');
      }
    } catch (error) {
      console.error('Error connecting to folder:', error);
      toast.error(`Failed to connect: ${error.message}`);
    }
  };

  // Upload lesson note
  const onUploadLessonNote = async (data) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Convert week to number
      const uploadData = {
        ...data,
        week: parseInt(data.week)
      };
      
      const response = await authenticatedFetch(`${backendUrl}/api/teacher/lesson-notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(uploadData)
      });

      if (response.ok) {
        toast.success("Lesson note uploaded successfully");

        setIsUploadDialogOpen(false);
        resetUpload();
        loadDashboard(); // Refresh dashboard
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to upload lesson note');
      }
    } catch (error) {
      console.error('Error uploading lesson note:', error);
      toast.error(`Failed to upload: ${error.message}`);
    }
  };

  // Delete lesson note
  const deleteLessonNote = async (noteId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await authenticatedFetch(`${backendUrl}/api/teacher/lesson-notes/${noteId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success("Lesson note deleted successfully");
        loadDashboard(); // Refresh dashboard
      } else {
        throw new Error('Failed to delete lesson note');
      }
    } catch (error) {
      console.error('Error deleting lesson note:', error);
      toast.error("Failed to delete lesson note");
    }
  };

  // View submission - Mobile Safe Version using token-based URL
  const handleViewSubmission = async (noteId, fileName = 'lesson_note.pdf') => {
    try {
      toast.info(`📄 Loading ${fileName}...`, { duration: 2000 });
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Create a temporary view token
      const tokenResponse = await authenticatedFetch(`${backendUrl}/api/teacher/create-submission-view-token/${noteId}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!tokenResponse.ok) {
        throw new Error('Failed to prepare document view');
      }
      
      const { token } = await tokenResponse.json();
      const viewUrl = `${backendUrl}/api/public/view-submission/${token}`;
      
      // Use real <a> tag click to ensure mobile browsers handle it properly
      const link = document.createElement('a');
      link.href = viewUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      
      // Append, click, remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`📄 Opening ${fileName}`);
    } catch (error) {
      console.error('Error opening submission:', error);
      toast.error("Failed to open submission. The file may need to be re-uploaded.");
    }
  };

  // Open delivery status modal
  const openDeliveryModal = (note) => {
    setSelectedDeliveryNote(note);
    setDeliveryStatus(note.delivery_status || '');
    setDeliveryRemarks(note.delivery_remarks || '');
    setDeliveryActionPlan(note.delivery_action_plan || '');
    setShowDeliveryModal(true);
  };

  // Close delivery status modal
  const closeDeliveryModal = () => {
    setShowDeliveryModal(false);
    setSelectedDeliveryNote(null);
    setDeliveryStatus('');
    setDeliveryRemarks('');
    setDeliveryActionPlan('');
  };

  // Update lesson delivery status
  const updateDeliveryStatus = async () => {
    if (!selectedDeliveryNote) return;
    
    if (!deliveryStatus) {
      toast.error('Please select a delivery status');
      return;
    }
    
    if (deliveryStatus === 'not_delivered' && !deliveryRemarks.trim()) {
      toast.error('Please provide a reason for not delivering the lesson');
      return;
    }
    
    setSavingDeliveryStatus(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await authenticatedFetch(`${backendUrl}/api/teacher/update-delivery-status/${selectedDeliveryNote.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          delivery_status: deliveryStatus,
          remarks: deliveryRemarks.trim(),
          action_plan: deliveryActionPlan.trim()
        }),
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success(deliveryStatus === 'delivered' ? '✅ Marked as delivered!' : '📝 Status updated with remarks');
        closeDeliveryModal();
        loadDashboard(); // Refresh to show updated status
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update delivery status');
      }
    } catch (error) {
      console.error('Error updating delivery status:', error);
      toast.error(error.message || 'Failed to update delivery status');
    } finally {
      setSavingDeliveryStatus(false);
    }
  };

  // Open respond to comment modal
  const openRespondModal = (note) => {
    setSelectedCommentNote(note);
    setResponseNote(note.teacher_response || '');
    setShowRespondModal(true);
  };

  // Close respond modal
  const closeRespondModal = () => {
    setShowRespondModal(false);
    setSelectedCommentNote(null);
    setResponseNote('');
  };

  // Submit response to headteacher's comment
  const submitResponse = async () => {
    if (!selectedCommentNote) return;
    
    if (!responseNote.trim()) {
      toast.error('Please enter your response');
      return;
    }
    
    setSubmittingResponse(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await authenticatedFetch(`${backendUrl}/api/teacher/respond-to-comment/${selectedCommentNote.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          response_note: responseNote.trim()
        }),
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('✅ Response sent to headteacher!');
        closeRespondModal();
        loadDashboard(); // Refresh to show updated status
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit response');
      }
    } catch (error) {
      console.error('Error submitting response:', error);
      toast.error(error.message || 'Failed to submit response');
    } finally {
      setSubmittingResponse(false);
    }
  };

  // Copy referral link to clipboard
  const copyReferralLink = () => {
    if (referralLink) {
      navigator.clipboard.writeText(referralLink).then(() => {
        toast.success("Referral link copied to clipboard!");
      }).catch(() => {
        toast.error("Failed to copy link");
      });
    }
  };

  // Delete admin lesson note
  const deleteNote = async (note) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await authenticatedFetch(`${backendUrl}/api/teacher/admin-lesson-notes/${note.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success(`✅ ${note.file_name || 'Lesson note'} deleted successfully!`);
        // Refresh admin lesson notes
        await loadAdminLessonNotes();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete lesson note');
      }
    } catch (error) {
      console.error('Error deleting admin lesson note:', error);
      const noteTitle = typeof note.title === 'string' ? note.title : (note.file_name || 'lesson note');
      toast.error(`❌ Failed to delete ${noteTitle}: ${error.message}`);
    }
  };


  // Open note in rich text editor
  const openNoteEditor = async (note) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    setLoadingNoteEdit(true);
    try {
      const res = await authenticatedFetch(`${backendUrl}/api/teacher/lesson-note-content/${note.id}`);
      if (res.ok) {
        const data = await res.json();
        setEditingNote({ ...data, file_name: note.file_name, class_name: note.class_name, term: note.term });
        setEditNoteContent(data.content_html || '');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to load note content');
      }
    } catch (e) { toast.error('Error loading note for editing'); }
    setLoadingNoteEdit(false);
  };

  // Save edited note content
  const saveNoteEdit = async () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    if (!editingNote) return;
    setSavingNoteEdit(true);
    try {
      const res = await authenticatedFetch(`${backendUrl}/api/teacher/lesson-note-content/${editingNote.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content_html: editNoteContent })
      });
      if (res.ok) {
        toast.success('Lesson note saved successfully');
        setEditingNote(null);
        setEditNoteContent('');
      } else { const err = await res.json(); toast.error(err.detail || 'Save failed'); }
    } catch (e) { toast.error('Error saving note'); }
    setSavingNoteEdit(false);
  };


  useEffect(() => {
    loadDashboard();
    loadPricing();
    loadUserProfile(); // Load profile on initial load for signature/stamp in letter preview

    loadUserNotifications(); // Load user notifications (payment links, etc.)
    
    // Refresh notifications every 60 seconds
    const notificationInterval = setInterval(loadUserNotifications, 60000);
    
    // Handle subscription status from URL parameters (post-payment redirect)
    const urlParams = new URLSearchParams(window.location.search);
    const subscriptionStatus = urlParams.get('subscription');
    
    if (subscriptionStatus) {
      // Show appropriate notification based on payment result
      setTimeout(() => {
        if (subscriptionStatus === 'activated') {
          toast.success('🎉 Payment Successful! Your subscription has been activated.');
          // Reload dashboard to get updated subscription status
          setTimeout(() => {
            loadDashboard();
          }, 1000);
        } else if (subscriptionStatus === 'failed') {
          toast.error('❌ Payment Failed. Please try again or contact support.');
        } else if (subscriptionStatus === 'error') {
          toast.error('⚠️ Payment verification error. Please contact support if you were charged.');
        }
      }, 500);
      
      // Clean up URL parameters after handling
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
    
    // Handle payment success from escalation
    const paymentStatus = urlParams.get('payment');
    const paymentRef = urlParams.get('ref');
    if (paymentStatus === 'success' && paymentRef) {
      toast.success('🎉 Payment Successful! Your application has been submitted to the District Office.');
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
      loadUserNotifications(); // Refresh notifications
    }
    
    return () => clearInterval(notificationInterval);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-green-600 mb-4" />
            <h2 className="text-xl font-semibold text-gray-700">Loading Dashboard...</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 p-3 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        
        {/* Header - Mobile Responsive */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="w-full sm:w-auto">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Welcome, {dashboardData?.user?.name}
            </h1>
            <div className="flex items-center gap-2">
              <p className="text-sm sm:text-base text-gray-600">Teacher Dashboard</p>
              {dataFromCache && (
                <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-300">
                  <Clock className="h-3 w-3 mr-1" />
                  Cached
                </Badge>
              )}
              {isOffline && (
                <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-300">
                  Offline
                </Badge>
              )}
            </div>
            {subscription && (
              <Badge className="mt-2" variant={subscription.status === 'active' ? 'default' : 'secondary'}>
                {subscription.status === 'active' ? 'Subscribed' : 'No Active Subscription'}
              </Badge>
            )}
          </div>
          
          {/* Buttons Container - Two rows on mobile */}
          <div className="w-full sm:w-auto space-y-2 sm:space-y-0">
            {/* Row 1: Subscribe & Connect to Headteacher */}
            <div className="flex gap-2 sm:gap-3 justify-start sm:justify-end">
              {/* Refresh Button - For cache issues */}
              {dataFromCache && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    // Clear cache and reload
                    localStorage.removeItem('offline_data_cache');
                    window.location.reload();
                  }}
                  className="text-xs"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Refresh
                </Button>
              )}
              
              {/* Subscribe Button - Always show for management */}
              <Dialog open={isSubscribeDialogOpen} onOpenChange={setIsSubscribeDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-purple-600 hover:bg-purple-700 text-xs sm:text-sm px-3 sm:px-4">
                    <CreditCard className="h-4 w-4 mr-1 sm:mr-2" />
                    <span>{subscription && subscription.status === 'active' ? 'Manage' : 'Subscribe'}</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-[95vw] sm:max-w-4xl max-h-[85vh] overflow-y-auto p-4 sm:p-6">
                  <DialogHeader className="pb-2 sm:pb-4">
                    <DialogTitle className="text-base sm:text-xl">Subscribe to Lesson Notes</DialogTitle>
                    <DialogDescription className="text-xs sm:text-sm">
                      Select classes or subjects to subscribe
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-3 sm:space-y-6">
                    {/* KG & Primary Classes - Compact on mobile */}
                    <div>
                      <h3 className="text-sm sm:text-lg font-medium mb-1 sm:mb-3">KG & Primary Classes</h3>
                      <p className="text-[10px] sm:text-sm text-gray-600 mb-2">1 class = standard price, 2+ = multiple</p>
                      <div className="grid grid-cols-3 sm:grid-cols-3 gap-1 sm:gap-3">
                        {kgPrimaryClasses.map((className) => (
                          <div key={className} className="flex items-center space-x-1 sm:space-x-2">
                            <Checkbox
                              id={className}
                              checked={selectedClassesSubjects.includes(className)}
                              onCheckedChange={(checked) => handleSelectionChange(className, checked)}
                              className="h-4 w-4 sm:h-5 sm:w-5"
                            />
                            <label htmlFor={className} className="text-[10px] sm:text-sm font-medium cursor-pointer">
                              {className}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="border-t pt-3 sm:pt-6">
                      <h3 className="text-sm sm:text-lg font-medium mb-1 sm:mb-3">JHS Subjects</h3>
                      <p className="text-[10px] sm:text-sm text-gray-600 mb-2">2 subjects = standard, 3+ = +50% each extra</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 sm:gap-3">
                        {jhsSubjects.map((subject) => (
                          <div key={subject} className="flex items-center space-x-1 sm:space-x-2">
                            <Checkbox
                              id={subject}
                              checked={selectedClassesSubjects.includes(subject)}
                              onCheckedChange={(checked) => handleSelectionChange(subject, checked)}
                              className="h-4 w-4 sm:h-5 sm:w-5"
                            />
                            <label htmlFor={subject} className="text-[10px] sm:text-sm font-medium cursor-pointer leading-tight">
                              {subject}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Price Display - Compact on mobile */}
                    {selectedClassesSubjects.length > 0 && (
                      <div className="bg-purple-50 p-2 sm:p-4 rounded-lg border border-purple-200">
                        <div className="flex justify-between items-center gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-xs sm:text-base truncate">
                              {selectedClassesSubjects.length} item(s) selected
                            </p>
                            <p className="text-[10px] sm:text-sm text-gray-600">Base: GHS {standardPrice}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-lg sm:text-2xl font-bold text-purple-600">GHS {subscriptionPrice}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Fixed footer on mobile for easy access to payment button */}
                  <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-3 sm:pt-4 mt-2 border-t sticky bottom-0 bg-white">
                    <Button variant="outline" onClick={() => setIsSubscribeDialogOpen(false)} className="w-full sm:w-auto text-sm">
                      Cancel
                    </Button>
                    <Button 
                      onClick={(e) => {
                        console.log("🔘 Proceed to Payment button clicked");
                        console.log("Current selectedClassesSubjects:", selectedClassesSubjects);
                        console.log("Button disabled state:", selectedClassesSubjects.length === 0);
                        
                        // Ensure we prevent any event bubbling issues
                        e.preventDefault();
                        e.stopPropagation();
                        
                        try {
                          processSubscription();
                        } catch (error) {
                          console.error("Error calling processSubscription:", error);
                          toast.error(`Button click error: ${error.message}`);
                        }
                      }}
                      disabled={selectedClassesSubjects.length === 0}
                      className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto text-sm sm:text-base font-semibold"
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      Pay GHS {subscriptionPrice}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

            <Dialog open={isConnectDialogOpen} onOpenChange={setIsConnectDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-green-600 hover:bg-green-700 text-xs sm:text-base px-2 sm:px-4">
                  <LinkIcon className="h-4 w-4 mr-1 sm:mr-2" />
                  <span>Connect to Headteacher</span>
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Connect to Folder</DialogTitle>
                  <DialogDescription>
                    Enter the connect token provided by your headteacher
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmitConnect(onConnectFolder)}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Connect Token</label>
                      <Input
                        {...registerConnect('connect_token')}
                        placeholder="Enter connect token"
                        className="font-mono"
                      />
                      {errorsConnect.connect_token && (
                        <p className="text-sm text-red-500 mt-1">{errorsConnect.connect_token.message}</p>
                      )}
                    </div>
                  </div>
                  
                  <DialogFooter className="mt-6">
                    <Button type="button" variant="outline" onClick={() => setIsConnectDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit">Connect</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
              <DialogTrigger asChild>
                {/* Upload Lesson Note button removed - teachers access admin uploaded lessons */}
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Upload Lesson Note</DialogTitle>
                  <DialogDescription>
                    Upload a lesson note to one of your connected folders
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmitUpload(onUploadLessonNote)}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Folder</label>
                      <select
                        {...registerUpload('folder_id')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        <option value="">Select Folder</option>
                        {dashboardData?.folders?.map((folder) => (
                          <option key={folder.id} value={folder.id}>
                            {folder.class_or_subject}
                          </option>
                        ))}
                      </select>
                      {errorsUpload.folder_id && (
                        <p className="text-sm text-red-500 mt-1">{errorsUpload.folder_id.message}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Week (1-18)</label>
                        <Input
                          type="number"
                          min="1"
                          max="18"
                          {...registerUpload('week', { valueAsNumber: true })}
                          placeholder="Week number"
                        />
                        {errorsUpload.week && (
                          <p className="text-sm text-red-500 mt-1">{errorsUpload.week.message}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2">Class Level</label>
                        <Input
                          {...registerUpload('class_level')}
                          placeholder="e.g., KG 1, Class 3"
                        />
                        {errorsUpload.class_level && (
                          <p className="text-sm text-red-500 mt-1">{errorsUpload.class_level.message}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Subject</label>
                      <Input
                        {...registerUpload('subject')}
                        placeholder="Subject name"
                      />
                      {errorsUpload.subject && (
                        <p className="text-sm text-red-500 mt-1">{errorsUpload.subject.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">File URL</label>
                      <Input
                        {...registerUpload('file_url')}
                        placeholder="URL to your lesson note file"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        For now, provide a URL to your uploaded file (Google Drive, Dropbox, etc.)
                      </p>
                      {errorsUpload.file_url && (
                        <p className="text-sm text-red-500 mt-1">{errorsUpload.file_url.message}</p>
                      )}
                    </div>
                  </div>
                  
                  <DialogFooter className="mt-6">
                    <Button type="button" variant="outline" onClick={() => setIsUploadDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit">Upload</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            </div>
            
            {/* Row 2: Alerts & Logout */}
            <div className="flex gap-2 sm:gap-3 justify-start sm:justify-end">
            {/* Notification Bell with Dropdown */}
            <div className="relative">
              <Button 
                onClick={() => {
                  const willOpen = !showNotificationPanel;
                  setShowNotificationPanel(willOpen);
                  if (willOpen) markAllNotificationsRead();
                }}
                variant="outline" 
                className="relative text-xs sm:text-sm px-3 sm:px-4"
                data-testid="notification-bell-btn"
              >
                <Bell className="h-4 w-4 mr-1 sm:mr-2" />
                <span>Alerts</span>
                {unreadNotificationCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {unreadNotificationCount > 9 ? '9+' : unreadNotificationCount}
                  </span>
                )}
              </Button>
              
              {/* Notification Dropdown Panel */}
              {showNotificationPanel && (
                <div className="fixed inset-0 sm:absolute sm:inset-auto sm:right-0 sm:mt-2 w-full sm:w-96 bg-white sm:rounded-lg shadow-xl border-0 sm:border border-gray-200 z-50 h-full sm:h-auto sm:max-h-[400px] overflow-hidden" data-testid="notification-panel">
                  <div className="p-4 sm:p-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white sm:rounded-t-lg flex justify-between items-center">
                    <h3 className="font-semibold flex items-center gap-2 text-lg sm:text-base">
                      <Bell className="h-5 w-5 sm:h-4 sm:w-4" />
                      Notifications
                      {unreadNotificationCount > 0 && (
                        <Badge className="bg-white/20 text-white">{unreadNotificationCount} new</Badge>
                      )}
                    </h3>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-white hover:bg-white/20 h-8 w-8 sm:h-6 sm:w-6 p-0"
                      onClick={() => setShowNotificationPanel(false)}
                    >
                      <X className="h-5 w-5 sm:h-4 sm:w-4" />
                    </Button>
                  </div>
                  
                  <div className="overflow-y-auto h-[calc(100%-60px)] sm:max-h-[320px]">
                    {loadingNotifications ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-green-600" />
                      </div>
                    ) : userNotifications.length === 0 ? (
                      <div className="py-8 text-center text-gray-500">
                        <Bell className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                        <p>No notifications yet</p>
                      </div>
                    ) : (
                      userNotifications.map((notification) => (
                        <div 
                          key={notification.id}
                          className={`p-3 border-b border-gray-100 ${!notification.read ? 'bg-green-50' : ''}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                              notification.notification_type === 'payment_request' 
                                ? 'bg-amber-100' 
                                : 'bg-blue-100'
                            }`}>
                              {notification.notification_type === 'payment_request' ? (
                                <CreditCard className="h-4 w-4 text-amber-600" />
                              ) : (
                                <Bell className="h-4 w-4 text-blue-600" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium ${!notification.read ? 'text-gray-900' : 'text-gray-600'}`}>
                                {notification.title}
                              </p>
                              <p className="text-xs text-gray-500 mt-1 line-clamp-3">{notification.message}</p>
                              <p className="text-[10px] text-gray-400 mt-1">
                                {new Date(notification.created_at).toLocaleDateString()} {new Date(notification.created_at).toLocaleTimeString()}
                              </p>
                              
                              {/* Payment Action Button */}
                              {notification.notification_type === 'payment_request' && notification.action_url && (
                                <Button 
                                  size="sm" 
                                  className="mt-2 bg-amber-500 hover:bg-amber-600 text-white"
                                  onClick={() => handlePaymentClick(notification)}
                                  data-testid={`pay-btn-${notification.id}`}
                                >
                                  <ExternalLink className="h-3 w-3 mr-1" />
                                  Pay GHS {notification.related_data?.amount?.toFixed(2) || '25.00'}
                                </Button>
                              )}
                            </div>
                            {!notification.read && (
                              <div className="w-2 h-2 bg-green-600 rounded-full shrink-0 mt-1"></div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Logout Button */}
            <Button 
              onClick={handleLogout} 
              variant="outline" 
              className="text-red-600 border-red-600 hover:bg-red-50 text-xs sm:text-sm px-3 sm:px-4"
            >
              <LogOut className="h-4 w-4 mr-1 sm:mr-2" />
              <span>Logout</span>
            </Button>
            </div>
          </div>
        </div>

        {/* User Tutorial Guide - Collapsible (moved to top) */}
        <Dialog>
          <DialogTrigger asChild>
            <Button className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold py-6 text-base sm:text-lg shadow-lg">
              <BookOpen className="h-5 w-5 mr-2" />
              Learn How To Use The App
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-blue-900 text-xl sm:text-2xl">
                <BookOpen className="h-6 w-6" />
                How to Use Teacher Dashboard
              </DialogTitle>
              <DialogDescription className="text-blue-700">
                Follow these simple steps to subscribe, connect with your headteacher, and manage lesson notes
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              {/* Step 1 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-base">
                    1
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1">Subscribe to Your Class/Subjects</h4>
                  <p className="text-sm text-gray-700">
                    Click the <span className="font-semibold text-purple-600">"Subscribe"</span> button. If you're a KG or Primary teacher, select your class. If you're a JHS teacher, select your subjects. This gives you access to all weekly lesson notes.
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-base">
                    2
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1">Ask Your Headteacher to Create Account</h4>
                  <p className="text-sm text-gray-700">
                    Request your headteacher to create an account and set up a folder for you. This enables seamless submission of lesson notes.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-base">
                    3
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1">Get Connection Token from Headteacher</h4>
                  <p className="text-sm text-gray-700">
                    Your headteacher will provide you with a <span className="font-semibold text-green-600">connect token code</span>. This links your account to their dashboard for lesson note submissions.
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-base">
                    4
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1">Connect to Headteacher's Folder</h4>
                  <p className="text-sm text-gray-700">
                    Click <span className="font-semibold text-green-600">"Connect Folder"</span> button, paste the token code, and click connect. You're now connected to your headteacher!
                  </p>
                </div>
              </div>

              {/* Step 5 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-base">
                    5
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1">Access Lesson Notes Tab</h4>
                  <p className="text-sm text-gray-700">
                    Click on <span className="font-semibold text-orange-600">"Lesson Notes"</span> tab to view all available lesson notes. You'll see weekly lesson notes organized by week numbers.
                  </p>
                </div>
              </div>

              {/* Step 6 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-base">
                    6
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1">Download Lesson Notes (ZIP/PDF)</h4>
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold text-orange-600">• KG/Primary teachers:</span> Downloads as ZIP file. Unzip the folder using your phone's file manager or a free unzip app.
                    <br />
                    <span className="font-semibold text-indigo-600">• JHS teachers:</span> Direct PDF download ready to use.
                  </p>
                </div>
              </div>

              {/* Step 7 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-base">
                    7
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1">Submit to Headteacher</h4>
                  <p className="text-sm text-gray-700">
                    Click <span className="font-semibold text-red-600">"Upload to Headteacher"</span> button.
                    <br />
                    <span className="font-semibold text-orange-600">• KG/Primary teachers:</span> Select the unzipped PDF files from your folder.
                    <br />
                    <span className="font-semibold text-indigo-600">• JHS teachers:</span> Direct submission (no file selection needed).
                    <br />
                    <span className="font-semibold text-green-600">✅ Done!</span> Your lesson notes for the week are submitted to your headteacher.
                  </p>
                </div>
              </div>

              {/* Step 8 - Add to Home Screen */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-base">
                    8
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1">Add App to Home Screen (Optional)</h4>
                  <p className="text-sm text-gray-700 mb-2">
                    For quick access without typing the domain name every time, install the app on your device:
                  </p>
                  <div className="ml-4 space-y-2">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-blue-600">• On Android/Desktop:</span> Click the <span className="font-semibold">"Add to Home Screen"</span> button that appears on your dashboard, or click the install icon in your browser's address bar.
                    </p>
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-blue-600">• On iPhone/iPad:</span>
                      <br />
                      1. Tap the Share button 📤 at the bottom
                      <br />
                      2. Scroll down and tap "Add to Home Screen"
                      <br />
                      3. Tap "Add" to confirm
                    </p>
                  </div>
                  <p className="text-sm text-gray-700 mt-2">
                    <span className="font-semibold text-purple-600">🎉 Enjoy!</span> Access the app like a native app from your home screen.
                  </p>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* WhatsApp Channel - Only for Subscribed Teachers */}
        {subscription && subscription.status === 'active' && (
          <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-500 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg sm:text-xl font-bold text-green-800 mb-1">
                    📢 Join Our WhatsApp Channel
                  </h3>
                  <p className="text-sm sm:text-base text-green-700 mb-3">
                    Get instant notifications when new lesson notes are uploaded! Stay updated with the latest materials for your class.
                  </p>
                  <a 
                    href="https://whatsapp.com/channel/0029VaH7Lu47dmeYeQPmUJ0C" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-block"
                  >
                    <Button className="bg-green-600 hover:bg-green-700 text-white font-semibold">
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      Join WhatsApp Channel
                    </Button>
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* PWA Install Prompt */}
        <InstallPWA />


        {/* Main Tabs - Mobile Responsive */}
        <Tabs value={mainTab} onValueChange={setMainTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 sm:grid-cols-6 h-auto gap-1">
            <TabsTrigger value="folders" className="text-xs sm:text-sm py-2 sm:py-3">
              My Folder
            </TabsTrigger>
            <TabsTrigger value="notes" className="text-xs sm:text-sm py-2 sm:py-3">
              Notes Submitted
            </TabsTrigger>
            <TabsTrigger value="subscription" className="text-xs sm:text-sm py-2 sm:py-3">
              My Notes
            </TabsTrigger>
            <TabsTrigger value="workoutput" className="text-xs sm:text-sm py-2 sm:py-3">
              Work Output
            </TabsTrigger>
            {authUser?.sector !== 'private' && (
            <TabsTrigger value="applications" className="text-xs sm:text-sm py-2 sm:py-3" onClick={loadMyApplications}>
              Applications
            </TabsTrigger>
            )}
            <TabsTrigger value="profile" className="text-xs sm:text-sm py-2 sm:py-3" onClick={loadUserProfile} data-testid="tab-profile">
              Profile
            </TabsTrigger>
            {authUser?.sector !== 'private' && (
            <TabsTrigger value="promotion_exams" className="text-xs sm:text-sm py-2 sm:py-3" data-testid="tab-promotion-exams">
              <GraduationCap className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
              Promotion Exams
            </TabsTrigger>
            )}
          </TabsList>

          {/* Folders Tab */}
          <TabsContent value="folders">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Folder className="h-5 w-5" />
                  Connected Folder
                </CardTitle>
                <CardDescription>
                  Folder you have access to for uploading lesson notes
                </CardDescription>
              </CardHeader>
              <CardContent>
                {dashboardData?.folders?.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {dashboardData.folders.map((folder) => (
                      <Card key={folder.id} className="border-l-4 border-l-green-500">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex items-center gap-2">
                              <Folder className="h-4 w-4 text-green-600" />
                              <h3 className="font-semibold">{folder.class_or_subject}</h3>
                            </div>
                            <Badge variant={folder.is_active ? "default" : "secondary"}>
                              {folder.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </div>
                          
                          <div className="space-y-2 text-sm text-gray-600">
                            <p><strong>Created:</strong> {new Date(folder.created_at).toLocaleDateString()}</p>
                            <p><strong>Token:</strong> {folder.connect_token.substring(0, 8)}...</p>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Folder className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-600 mb-2">No Folders Connected</h3>
                    <p className="text-gray-500 mb-4">
                      Use a connect token from your headteacher to access folders
                    </p>
                    <Button onClick={() => setIsConnectDialogOpen(true)} className="bg-green-600 hover:bg-green-700">
                      <LinkIcon className="h-4 w-4 mr-2" />
                      Connect to Folder
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Lesson Notes Tab - Shows only submitted notes to headteacher */}
          <TabsContent value="notes">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Uploaded Notes
                </CardTitle>
                <CardDescription>
                  Lesson notes submitted to your headteacher for review
                </CardDescription>
              </CardHeader>
              <CardContent>
                {(() => {
                  // Filter to show only submitted notes
                  const submittedNotes = lessonNotes.filter(note => note.submitted_to_headteacher);
                  
                  if (submittedNotes.length === 0) {
                    return (
                      <div className="text-center py-12">
                        <FileText className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                        <h3 className="text-lg font-medium text-gray-600 mb-2">No Submitted Notes</h3>
                        <p className="text-gray-500">
                          You haven't submitted any lesson notes to your headteacher yet
                        </p>
                      </div>
                    );
                  }
                  
                  // Group by folder_name, then by week
                  const groupedByFolder = submittedNotes.reduce((acc, note) => {
                    const folderName = note.folder_name || 'Unknown Folder';
                    if (!acc[folderName]) {
                      acc[folderName] = {};
                    }
                    const week = note.week || 'Unknown';
                    if (!acc[folderName][week]) {
                      acc[folderName][week] = [];
                    }
                    acc[folderName][week].push(note);
                    return acc;
                  }, {});
                  
                  return (
                    <div className="space-y-6">
                      {Object.keys(groupedByFolder).map(folderName => (
                        <div key={folderName} className="mb-4">
                          <h4 className="font-semibold text-base sm:text-lg text-gray-800 mb-3 border-b pb-2 flex items-center gap-2">
                            <Folder className="h-5 w-5 text-blue-600" />
                            {folderName}
                            <Badge variant="outline" className="ml-2">
                              {Object.values(groupedByFolder[folderName]).flat().length} notes
                            </Badge>
                          </h4>
                          
                          {Object.keys(groupedByFolder[folderName])
                            .sort((a, b) => Number(b) - Number(a)) // Sort weeks descending (newest first)
                            .map(week => {
                              const weekNotes = groupedByFolder[folderName][week];
                              const isExpanded = isUploadedWeekExpanded(folderName, week);
                              const notesCount = weekNotes.length;
                              
                              return (
                                <div key={week} className="mb-2 ml-2 sm:ml-4">
                                  {/* Clickable Week Folder Header */}
                                  <button
                                    onClick={() => toggleUploadedWeekFolder(folderName, week)}
                                    className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 rounded-lg border border-blue-200 transition-all duration-200 group"
                                  >
                                    <div className="flex items-center gap-2 sm:gap-3">
                                      {isExpanded ? (
                                        <FolderOpen className="h-5 w-5 text-blue-600" />
                                      ) : (
                                        <Folder className="h-5 w-5 text-blue-600" />
                                      )}
                                      <span className="font-medium text-sm sm:text-base text-gray-800">
                                        Week {week}
                                      </span>
                                      <Badge variant="secondary" className="bg-blue-100 text-blue-700 text-xs">
                                        {notesCount} {notesCount === 1 ? 'note' : 'notes'}
                                      </Badge>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs text-gray-500 hidden sm:inline">
                                        {isExpanded ? 'Click to collapse' : 'Click to expand'}
                                      </span>
                                      {isExpanded ? (
                                        <ChevronDown className="h-5 w-5 text-gray-500 group-hover:text-blue-600 transition-colors" />
                                      ) : (
                                        <ChevronRight className="h-5 w-5 text-gray-500 group-hover:text-blue-600 transition-colors" />
                                      )}
                                    </div>
                                  </button>
                                  
                                  {/* Expandable Notes Container */}
                                  {isExpanded && (
                                    <div className="mt-2 space-y-2 ml-2 sm:ml-4 animate-in slide-in-from-top-2 duration-200">
                                      {weekNotes.map((note) => (
                                        <div 
                                          key={note.id}
                                          className="bg-gray-50 p-3 rounded-lg border-l-4 border-l-blue-500"
                                        >
                                          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                                            <div className="flex-1 w-full">
                                              <h6 className="font-medium text-sm sm:text-base text-blue-800">
                                                {note.subject}
                                              </h6>
                                              <p className="text-xs sm:text-sm text-gray-600">
                                                {note.class_level} • Term {note.term || 'N/A'}
                                              </p>
                                              <p className="text-xs text-gray-500 mt-1">
                                                Submitted: {note.submitted_at ? new Date(note.submitted_at).toLocaleString() : new Date(note.created_at).toLocaleDateString()}
                                              </p>
                                            </div>
                                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                                              {/* Comment Button - Inline with other buttons */}
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => setExpandedComments(prev => ({
                                                  ...prev,
                                                  [note.id]: !prev[note.id]
                                                }))}
                                                className={`relative text-xs sm:text-sm ${
                                                  note.headteacher_comment 
                                                    ? 'border-amber-500 text-amber-600 hover:bg-amber-50' 
                                                    : 'border-gray-300 text-gray-400 cursor-not-allowed opacity-50'
                                                }`}
                                                disabled={!note.headteacher_comment}
                                                data-testid={`comment-btn-${note.id}`}
                                              >
                                                <MessageSquare className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                                                Comment
                                                {/* Notification dot when there's a comment */}
                                                {note.headteacher_comment && (
                                                  <span className="absolute -top-1 -right-1 h-3 w-3 bg-amber-500 rounded-full flex items-center justify-center">
                                                    <span className="text-[8px] text-white font-bold">!</span>
                                                  </span>
                                                )}
                                              </Button>
                                              {note.vetted ? (
                                                <Badge className="bg-green-600 hover:bg-green-700 text-center">
                                                  <CheckCircle className="h-3 w-3 mr-1" />
                                                  Vetted
                                                </Badge>
                                              ) : (
                                                <Badge variant="outline" className="border-yellow-500 text-yellow-700 text-center">
                                                  <Clock className="h-3 w-3 mr-1" />
                                                  Pending
                                                </Badge>
                                              )}
                                              {/* Delivery Status Button */}
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => openDeliveryModal(note)}
                                                className={`text-xs sm:text-sm ${
                                                  note.delivery_status === 'delivered' 
                                                    ? 'border-emerald-500 text-emerald-600 bg-emerald-50' 
                                                    : note.delivery_status === 'not_delivered'
                                                    ? 'border-red-500 text-red-600 bg-red-50'
                                                    : 'border-purple-500 text-purple-600 hover:bg-purple-50'
                                                }`}
                                                data-testid={`delivery-status-btn-${note.id}`}
                                              >
                                                {note.delivery_status === 'delivered' ? (
                                                  <>
                                                    <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                                                    Delivered
                                                  </>
                                                ) : note.delivery_status === 'not_delivered' ? (
                                                  <>
                                                    <XCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                                                    Not Delivered
                                                  </>
                                                ) : (
                                                  <>
                                                    <FileEdit className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                                                    Mark Delivery
                                                  </>
                                                )}
                                              </Button>
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleViewSubmission(note.id, note.file_name || `${note.subject}_week${note.week}.pdf`)}
                                                className="border-blue-600 text-blue-600 hover:bg-blue-50 text-xs sm:text-sm"
                                              >
                                                <Eye className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                                                View
                                              </Button>
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => deleteLessonNote(note.id)}
                                                className="border-red-600 text-red-600 hover:bg-red-50 text-xs sm:text-sm"
                                              >
                                                <Trash2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                                                Delete
                                              </Button>
                                            </div>
                                          </div>
                                          
                                          {/* Delivery Status Display (when not delivered) */}
                                          {note.delivery_status === 'not_delivered' && note.delivery_remarks && (
                                            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                                              <div className="flex items-start gap-2">
                                                <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                                                <div className="flex-1">
                                                  <p className="text-xs font-medium text-red-800">Reason for Non-Delivery:</p>
                                                  <p className="text-sm text-red-700 mt-1">{note.delivery_remarks}</p>
                                                  {note.delivery_action_plan && (
                                                    <>
                                                      <p className="text-xs font-medium text-red-800 mt-2">Action Plan:</p>
                                                      <p className="text-sm text-red-700 mt-1">{note.delivery_action_plan}</p>
                                                    </>
                                                  )}
                                                  {note.delivery_updated_at && (
                                                    <p className="text-[10px] text-red-500 mt-2">
                                                      Updated: {new Date(note.delivery_updated_at).toLocaleDateString()}
                                                    </p>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                          
                                          {/* Collapsible Headteacher Comment Display */}
                                          {note.headteacher_comment && expandedComments[note.id] && (
                                            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md animate-in slide-in-from-top-2 duration-200">
                                              <div className="flex items-start gap-2">
                                                <MessageSquare className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                                                <div className="flex-1">
                                                  <div className="flex items-center justify-between">
                                                    <p className="text-xs font-medium text-amber-800">Headteacher Comment:</p>
                                                    <button 
                                                      onClick={() => setExpandedComments(prev => ({...prev, [note.id]: false}))}
                                                      className="text-amber-600 hover:text-amber-800 text-xs"
                                                    >
                                                      ✕ Close
                                                    </button>
                                                  </div>
                                                  <p className="text-sm text-amber-900 mt-1">{note.headteacher_comment}</p>
                                                  {note.comment_date && (
                                                    <p className="text-[10px] text-amber-600 mt-1">
                                                      {new Date(note.comment_date).toLocaleDateString()}
                                                    </p>
                                                  )}
                                                  
                                                  {/* Teacher Response Section */}
                                                  {note.teacher_response ? (
                                                    <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded">
                                                      <p className="text-xs font-medium text-blue-800">Your Response:</p>
                                                      <p className="text-sm text-blue-900 mt-1">{note.teacher_response}</p>
                                                      {note.teacher_response_at && (
                                                        <p className="text-[10px] text-blue-600 mt-1">
                                                          Responded: {new Date(note.teacher_response_at).toLocaleDateString()}
                                                        </p>
                                                      )}
                                                    </div>
                                                  ) : (
                                                    <Button
                                                      size="sm"
                                                      onClick={() => openRespondModal(note)}
                                                      className="mt-3 bg-blue-600 hover:bg-blue-700 text-white text-xs"
                                                      data-testid={`respond-btn-${note.id}`}
                                                    >
                                                      <MessageSquare className="h-3 w-3 mr-1" />
                                                      Respond to Comment
                                                    </Button>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscription Lesson Notes Tab */}
          <TabsContent value="subscription">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <BookOpen className="h-5 w-5" />
                      My Lesson Notes (Subscription)
                    </CardTitle>
                    <CardDescription>
                      Access lesson notes for your subscribed classes/subjects
                    </CardDescription>
                  </div>
                  
                  {subscription && subscription.status === 'active' && (
                    <div className="flex gap-2">
                      {/* Old download/submit dialogs removed - now using individual lesson note cards */}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {subscription && subscription.status === 'active' ? (
                  <div className="space-y-4">
                    {/* Subscription Details */}
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-green-900">Active Subscription</h4>
                          <p className="text-sm text-green-700">
                            Classes/Subjects: {subscription.classes_subjects?.map(cs => typeof cs === 'string' ? cs : `${cs.class_name} - ${cs.subject}`).join(', ')}
                          </p>
                        </div>
                        <Badge className="bg-green-600">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Active
                        </Badge>
                      </div>
                    </div>

                    {/* Available Lesson Notes */}
                    <div className="space-y-3">
                      <h4 className="font-medium">Available Lesson Notes</h4>
                      
                      {adminLessonNotes.length > 0 ? (
                        (() => {
                          // Sort by newest first (created_at descending)
                          const sortedNotes = [...adminLessonNotes].sort((a, b) => {
                            const dateA = new Date(a.created_at || 0);
                            const dateB = new Date(b.created_at || 0);
                            return dateB - dateA; // Newest first
                          });
                          
                          // Group lesson notes by class and week
                          const groupedNotes = {};
                          sortedNotes.forEach(note => {
                            const className = note.class_name || 'Unknown Class';
                            const week = note.week || 'Unknown Week';
                            
                            if (!groupedNotes[className]) {
                              groupedNotes[className] = {};
                            }
                            if (!groupedNotes[className][week]) {
                              groupedNotes[className][week] = [];
                            }
                            groupedNotes[className][week].push(note);
                          });

                          return Object.keys(groupedNotes).map(className => (
                            <div key={className} className="mb-4 sm:mb-6">
                              <h4 className="font-semibold text-base sm:text-lg text-gray-800 mb-2 sm:mb-3 border-b pb-2">
                                📚 {className}
                              </h4>
                              {Object.keys(groupedNotes[className]).sort((a, b) => {
                                // Sort weeks numerically if they're numbers
                                const weekA = parseInt(a) || 0;
                                const weekB = parseInt(b) || 0;
                                return weekB - weekA; // Newest week first
                              }).map(week => {
                                const notesCount = groupedNotes[className][week].length;
                                const isExpanded = isWeekExpanded(className, week);
                                
                                return (
                                  <div key={week} className="mb-2 sm:mb-3 ml-2 sm:ml-4">
                                    {/* Clickable Week Folder Header */}
                                    <button
                                      onClick={() => toggleWeekFolder(className, week)}
                                      className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 rounded-lg border border-green-200 transition-all duration-200 group"
                                    >
                                      <div className="flex items-center gap-2 sm:gap-3">
                                        {isExpanded ? (
                                          <FolderOpen className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
                                        ) : (
                                          <Folder className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
                                        )}
                                        <span className="font-medium text-sm sm:text-base text-gray-800">
                                          Week {week}
                                        </span>
                                        <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs">
                                          {notesCount} {notesCount === 1 ? 'note' : 'notes'}
                                        </Badge>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-500 hidden sm:inline">
                                          {isExpanded ? 'Click to collapse' : 'Click to expand'}
                                        </span>
                                        {isExpanded ? (
                                          <ChevronDown className="h-5 w-5 text-gray-500 group-hover:text-green-600 transition-colors" />
                                        ) : (
                                          <ChevronRight className="h-5 w-5 text-gray-500 group-hover:text-green-600 transition-colors" />
                                        )}
                                      </div>
                                    </button>
                                    
                                    {/* Expandable Notes Container */}
                                    {isExpanded && (
                                      <div className="mt-2 space-y-2 sm:space-y-3 ml-2 sm:ml-4 animate-in slide-in-from-top-2 duration-200">
                                        {groupedNotes[className][week].map((note, index) => (
                                          <Card key={note.id || index} className="border-l-4 border-l-green-500">
                                            <CardContent className="p-3 sm:p-4">
                                              <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
                                                <div className="flex-1 w-full">
                                                  <h6 className="font-medium text-sm sm:text-base text-green-800">
                                                    {typeof note.title === 'string' ? note.title : (note.file_name || 'Lesson Note')}
                                                  </h6>
                                                  <p className="text-xs sm:text-sm text-gray-600">
                                                    {typeof note.subject === 'string' ? note.subject : (note.class_subject || 'Subject')} • Term {note.term}
                                                  </p>
                                                  <p className="text-xs text-gray-500 mt-1">
                                                    File: {note.file_name || 'Unknown'} • Type: {(note.file_type || 'pdf').toUpperCase()}
                                                  </p>
                                                  {note.created_at && (
                                                    <p className="text-xs text-gray-500">
                                                      Generated: {new Date(note.created_at).toLocaleDateString()}
                                                    </p>
                                                  )}
                                                </div>
                                                {/* Mobile: Stack buttons vertically, Desktop: Horizontal */}
                                                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                                                  <Badge variant="outline" className="bg-green-50 text-green-700 text-center">
                                                    {note.education_level?.toUpperCase() || 'JHS'}
                                                  </Badge>
                                                  
                                                  {/* EDIT */}
                                                  <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => openNoteEditor(note)}
                                                    disabled={loadingNoteEdit}
                                                    className="border-amber-600 text-amber-600 hover:bg-amber-50 text-xs sm:text-sm w-full sm:w-auto"
                                                    data-testid={`edit-note-btn-${note.id}`}
                                                  >
                                                    <FileEdit className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                                                    Edit
                                                  </Button>

                                                  {/* DOWNLOAD */}
                                                  {downloadLinks[note.id] ? (
                                                    <a
                                                      href={downloadLinks[note.id]}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      className="inline-flex items-center justify-center px-3 py-2 text-xs sm:text-sm font-medium rounded-md border-2 border-green-600 bg-green-600 text-white hover:bg-green-700 w-full sm:w-auto text-center"
                                                    >
                                                      <Download className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                                                      Open PDF
                                                    </a>
                                                  ) : (
                                                    <Button
                                                      size="sm"
                                                      variant="outline"
                                                      onClick={() => downloadIndividualNote(note)}
                                                      disabled={generatingLink[note.id]}
                                                      className="border-green-600 text-green-600 hover:bg-green-50 text-xs sm:text-sm w-full sm:w-auto"
                                                    >
                                                      {generatingLink[note.id] ? (
                                                        <>
                                                          <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 animate-spin" />
                                                          Preparing...
                                                        </>
                                                      ) : (
                                                        <>
                                                          <Download className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                                                          Download
                                                        </>
                                                      )}
                                                    </Button>
                                                  )}

                                                  {/* SUBMIT TO HEAD */}
                                                  <Button
                                                    size="sm"
                                                    variant="outline" 
                                                    onClick={() => submitToHeadteacher(note)}
                                                    className="border-blue-600 text-blue-600 hover:bg-blue-50 text-xs sm:text-sm w-full sm:w-auto"
                                                    data-testid={`upload-ht-btn-${note.id}`}
                                                  >
                                                    <Upload className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                                                    <span className="hidden sm:inline">Submit to Head</span>
                                                    <span className="sm:hidden">To Headteacher</span>
                                                  </Button>

                                                  {/* DELETE */}
                                                  <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => {
                                                      if (window.confirm(`Are you sure you want to delete "${note.file_name || 'this lesson note'}"?`)) {
                                                        deleteNote(note);
                                                      }
                                                    }}
                                                    className="border-red-600 text-red-600 hover:bg-red-50 text-xs sm:text-sm w-full sm:w-auto"
                                                    data-testid={`delete-note-btn-${note.id}`}
                                                  >
                                                    <Trash2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                                                    Delete
                                                  </Button>
                                                </div>
                                              </div>
                                            </CardContent>
                                          </Card>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ));
                        })()
                      ) : (
                        <p className="text-gray-500 text-center py-4">No lesson notes available yet</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CreditCard className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-600 mb-2">No Active Subscription</h3>
                    <p className="text-gray-500 mb-4">
                      Subscribe to access lesson notes for specific classes or subjects
                    </p>
                    <Button onClick={() => setIsSubscribeDialogOpen(true)} className="bg-purple-600 hover:bg-purple-700">
                      <CreditCard className="h-4 w-4 mr-2" />
                      Subscribe Now
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Work Output Tab */}
          <TabsContent value="workoutput">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-purple-600" />
                  Work Output (Exercises)
                </CardTitle>
                <CardDescription>
                  Track the number of exercises completed per subject per week
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!subscription ? (
                  <div className="text-center py-8">
                    <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Active Subscription</h3>
                    <p className="text-gray-600 mb-4">
                      Subscribe to access work output tracking for your subjects.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Work Output - JHS Teachers: Grouped by Class */}
                    {isJhsTeacher() ? (
                      <div className="space-y-4">
                        {jhsClasses.map((jhsClass) => (
                          <div key={jhsClass} className="border rounded-lg overflow-hidden">
                            {/* Class Header - Collapsible */}
                            <button
                              onClick={() => setExpandedWorkOutputClasses(prev => ({
                                ...prev,
                                [jhsClass]: !prev[jhsClass]
                              }))}
                              className={`w-full p-3 flex items-center justify-between text-left transition-colors ${
                                expandedWorkOutputClasses[jhsClass]
                                  ? 'bg-purple-100 border-b border-purple-200'
                                  : 'bg-gray-50 hover:bg-gray-100'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                {expandedWorkOutputClasses[jhsClass] ? (
                                  <ChevronDown className="h-4 w-4 text-purple-600" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-gray-500" />
                                )}
                                <span className="font-semibold text-gray-800">{jhsClass}</span>
                                <span className="text-xs text-gray-500">({getWorkOutputSubjects().length} subjects)</span>
                              </div>
                            </button>

                            {/* Class Content - Expanded */}
                            {expandedWorkOutputClasses[jhsClass] && (
                              <div className="overflow-x-auto">
                                <table className="w-full min-w-[800px]">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 sticky left-0 bg-gray-50 min-w-[150px]">
                                        Subjects
                                      </th>
                                      {workOutputWeeks.map(week => (
                                        <th key={week} className="px-2 py-2 text-center text-xs font-semibold text-gray-700 min-w-[60px]">
                                          Week {week}
                                        </th>
                                      ))}
                                      <th className="px-3 py-2 text-center text-xs font-semibold text-green-700 bg-green-50 min-w-[70px]">
                                        Total
                                      </th>
                                      <th className="px-3 py-2 text-center text-xs font-semibold text-purple-700 bg-purple-50 min-w-[80px]">
                                        Status
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200">
                                    {getWorkOutputSubjects().map((subject, index) => {
                                      const key = `${jhsClass}|${subject}`;
                                      return (
                                        <tr key={key} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                          <td className="px-3 py-2 text-sm font-medium text-gray-900 sticky left-0 bg-inherit border-r">
                                            {subject}
                                          </td>
                                          {workOutputWeeks.map(week => {
                                            const verification = getVerificationStatus(subject, week, jhsClass);
                                            const hasValue = workOutputData[key]?.[week] && parseInt(workOutputData[key][week]) > 0;
                                            return (
                                              <td key={week} className="px-1 py-1 text-center relative">
                                                <div className="flex flex-col items-center gap-1">
                                                  <Input
                                                    type="number"
                                                    min="0"
                                                    value={workOutputData[key]?.[week] ?? ''}
                                                    onChange={(e) => updateWorkOutput(subject, week, e.target.value, jhsClass)}
                                                    className={`h-8 w-14 text-center text-sm px-1 mx-auto ${
                                                      verification?.status === 'compliant' ? 'border-green-500 bg-green-50' :
                                                      verification?.status === 'non_compliant' ? 'border-red-500 bg-red-50' : ''
                                                    }`}
                                                    placeholder="0"
                                                  />
                                                  {verification?.status === 'compliant' && hasValue && (
                                                    <span className="text-[10px] text-green-600 font-medium">✓ Verified</span>
                                                  )}
                                                  {verification?.status === 'non_compliant' && hasValue && (
                                                    <div className="text-[10px] text-red-600 font-medium" title={verification.comment}>
                                                      ✗ Issue
                                                    </div>
                                                  )}
                                                </div>
                                              </td>
                                            );
                                          })}
                                          <td className="px-3 py-2 text-center bg-green-50 border-l">
                                            <span className="text-sm font-bold text-green-700">
                                              {calculateSubjectTotal(subject, jhsClass)}
                                            </span>
                                          </td>
                                          <td className="px-2 py-2 text-center bg-purple-50 border-l">
                                            {(() => {
                                              const verifiedCount = workOutputWeeks.filter(week => {
                                                const v = getVerificationStatus(subject, week, jhsClass);
                                                return v?.status === 'compliant' && workOutputData[key]?.[week] > 0;
                                              }).length;
                                              const totalEntered = workOutputWeeks.filter(week => 
                                                workOutputData[key]?.[week] && parseInt(workOutputData[key][week]) > 0
                                              ).length;
                                              
                                              if (totalEntered === 0) return <span className="text-xs text-gray-400">-</span>;
                                              if (verifiedCount === totalEntered) {
                                                return <Badge className="bg-green-600 text-[10px]">All Verified</Badge>;
                                              }
                                              return <span className="text-[10px] text-purple-600">{verifiedCount}/{totalEntered}</span>;
                                            })()}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      /* KG/Primary Teachers: Simple Table */
                      <div className="overflow-x-auto border rounded-lg">
                        <table className="w-full min-w-[800px]">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 sticky left-0 bg-gray-50 min-w-[150px]">
                                Subjects
                              </th>
                              {workOutputWeeks.map(week => (
                                <th key={week} className="px-2 py-2 text-center text-xs font-semibold text-gray-700 min-w-[60px]">
                                  Week {week}
                                </th>
                              ))}
                              <th className="px-3 py-2 text-center text-xs font-semibold text-green-700 bg-green-50 min-w-[70px]">
                                Total
                              </th>
                              <th className="px-3 py-2 text-center text-xs font-semibold text-purple-700 bg-purple-50 min-w-[80px]">
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {getWorkOutputSubjects().map((subject, index) => (
                              <tr key={subject} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="px-3 py-2 text-sm font-medium text-gray-900 sticky left-0 bg-inherit border-r">
                                  {subject}
                                </td>
                                {workOutputWeeks.map(week => {
                                  const verification = getVerificationStatus(subject, week);
                                  const hasValue = workOutputData[subject]?.[week] && parseInt(workOutputData[subject][week]) > 0;
                                  return (
                                    <td key={week} className="px-1 py-1 text-center relative">
                                      <div className="flex flex-col items-center gap-1">
                                        <Input
                                          type="number"
                                          min="0"
                                          value={workOutputData[subject]?.[week] ?? ''}
                                          onChange={(e) => updateWorkOutput(subject, week, e.target.value)}
                                          className={`h-8 w-14 text-center text-sm px-1 mx-auto ${
                                            verification?.status === 'compliant' ? 'border-green-500 bg-green-50' :
                                            verification?.status === 'non_compliant' ? 'border-red-500 bg-red-50' : ''
                                          }`}
                                          placeholder="0"
                                        />
                                        {verification?.status === 'compliant' && hasValue && (
                                          <span className="text-[10px] text-green-600 font-medium">✓ Verified</span>
                                        )}
                                        {verification?.status === 'non_compliant' && hasValue && (
                                          <div className="text-[10px] text-red-600 font-medium" title={verification.comment}>
                                            ✗ Issue
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                  );
                                })}
                                <td className="px-3 py-2 text-center bg-green-50 border-l">
                                  <span className="text-sm font-bold text-green-700">
                                    {calculateSubjectTotal(subject)}
                                  </span>
                                </td>
                                <td className="px-2 py-2 text-center bg-purple-50 border-l">
                                  {(() => {
                                    const verifiedCount = workOutputWeeks.filter(week => {
                                      const v = getVerificationStatus(subject, week);
                                      return v?.status === 'compliant' && workOutputData[subject]?.[week] > 0;
                                    }).length;
                                    const totalEntered = workOutputWeeks.filter(week => 
                                      workOutputData[subject]?.[week] && parseInt(workOutputData[subject][week]) > 0
                                    ).length;
                                    
                                    if (totalEntered === 0) return <span className="text-xs text-gray-400">-</span>;
                                    if (verifiedCount === totalEntered) {
                                      return <Badge className="bg-green-600 text-[10px]">All Verified</Badge>;
                                    }
                                    return <span className="text-[10px] text-purple-600">{verifiedCount}/{totalEntered}</span>;
                                  })()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Verification Comments Display */}
                    {Object.keys(workOutputVerification).some(subject => 
                      Object.values(workOutputVerification[subject] || {}).some(v => v?.status === 'non_compliant' && v?.comment)
                    ) && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-4">
                        <h5 className="text-sm font-semibold text-red-800 mb-2">⚠️ Headteacher Comments:</h5>
                        <div className="space-y-2">
                          {Object.entries(workOutputVerification).map(([subject, weeks]) => 
                            Object.entries(weeks || {}).map(([week, verification]) => 
                              verification?.status === 'non_compliant' && verification?.comment ? (
                                <div key={`${subject}-${week}`} className="text-sm text-red-700 bg-white p-2 rounded border border-red-200">
                                  <strong>{subject} (Week {week}):</strong> {verification.comment}
                                </div>
                              ) : null
                            )
                          )}
                        </div>
                      </div>
                    )}

                    {/* Submit Button */}
                    <div className="flex justify-end pt-4">
                      <Button 
                        onClick={saveWorkOutput}
                        disabled={savingWorkOutput}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {savingWorkOutput ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-2" />
                            Submit to Headteacher
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Info Note */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                      <p className="text-sm text-blue-800">
                        <strong>💡 Tip:</strong> Enter the number of exercises completed for each subject per week. 
                        The total will be automatically calculated. Click &quot;Submit to Headteacher&quot; to send for verification.
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Applications Tab */}
          <TabsContent value="applications">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileEdit className="h-5 w-5 text-blue-600" />
                  Applications
                </CardTitle>
                <CardDescription>
                  Create and manage your official application letters
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Sub-Tab Buttons */}
                <div className="flex flex-col sm:flex-row gap-2 border-b pb-4">
                  <Button
                    variant={applicationSubTab === 'create' ? 'default' : 'outline'}
                    onClick={() => setApplicationSubTab('create')}
                    className={applicationSubTab === 'create' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                    data-testid="create-application-btn"
                  >
                    <FileEdit className="h-4 w-4 mr-2" />
                    Create Application
                  </Button>
                  <Button
                    variant={applicationSubTab === 'submitted' ? 'default' : 'outline'}
                    onClick={() => {
                      setApplicationSubTab('submitted');
                      loadMyApplications();
                    }}
                    className={applicationSubTab === 'submitted' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                    data-testid="submitted-applications-btn"
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    Submitted Applications
                    {myApplications.length > 0 && (
                      <Badge className="ml-2 bg-white text-blue-600">{myApplications.length}</Badge>
                    )}
                  </Button>
                </div>

                {/* Create Application Sub-Tab */}
                {applicationSubTab === 'create' && (
                  <div className="space-y-4">
                    {/* Application Type Select */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Application Type</label>
                      <Select value={applicationType} onValueChange={setApplicationType}>
                        <SelectTrigger className="w-full" data-testid="application-type-select">
                          <SelectValue placeholder="Select application type..." />
                        </SelectTrigger>
                        <SelectContent>
                          {applicationTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Application Recipient Select */}
                    {applicationType && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Recipient</label>
                        <Select value={applicationRecipient} onValueChange={setApplicationRecipient}>
                          <SelectTrigger className="w-full" data-testid="application-recipient-select">
                            <SelectValue placeholder="Select recipient..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="headteacher">Headteacher</SelectItem>
                            <SelectItem value="district_director">District Director</SelectItem>
                          </SelectContent>
                        </Select>
                        {applicationRecipient === 'district_director' && (
                          <p className="text-xs text-blue-600">
                            Letter will be addressed to: The Director, Ghana Education Service, {userProfile?.district_name || 'your district'}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Plain Text Editor */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Write your application content (plain text)
                      </label>
                      <Textarea
                        value={applicationPlainText}
                        onChange={(e) => setApplicationPlainText(e.target.value)}
                        placeholder="Write the main content of your application here. The AI will format it into a professional official letter..."
                        className="min-h-[200px] resize-y"
                        data-testid="application-text-input"
                      />
                      <p className="text-xs text-gray-500">
                        Tip: Include the reason, dates, and any relevant details. The AI will add proper formatting, addresses, and formal language.
                      </p>
                    </div>

                    {/* Generate Button */}
                    <div className="flex justify-end">
                      <Button
                        onClick={generateApplicationLetter}
                        disabled={generatingLetter || !applicationType || !applicationPlainText.trim()}
                        className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                        data-testid="generate-letter-btn"
                      >
                        {generatingLetter ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="h-4 w-4 mr-2" />
                            Generate Letter
                          </>
                        )}
                      </Button>
                    </div>

                {/* Generated Letter - Tiptap Editor */}
                {showLetterPreview && generatedLetter && (
                  <div className="space-y-4 border-t pt-6">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        <FileText className="h-5 w-5 text-green-600" />
                        {letterSaved ? 'Application Letter (Saved)' : 'Edit Application Letter'}
                      </h3>
                      {letterSaved && (
                        <Badge className="bg-green-100 text-green-700">Saved</Badge>
                      )}
                    </div>
                    
                    {/* Tiptap A4 Editor */}
                    {!letterSaved ? (
                      <div className="space-y-3">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <p className="text-sm text-blue-700 flex items-center gap-2">
                            <FileEdit className="h-4 w-4" />
                            Review and edit the letter below before saving. Your edits will be preserved in the final PDF.
                          </p>
                        </div>
                        <div className="bg-white shadow-lg rounded-lg overflow-hidden border-2 border-gray-200 mx-auto" style={{ maxWidth: '210mm' }}>
                          <RichTextEditor
                            content={editedLetterHtml}
                            onChange={setEditedLetterHtml}
                            placeholder="Edit your application letter here..."
                            minHeight="500px"
                            className="[&_.ProseMirror]:px-6 [&_.ProseMirror]:sm:px-10 [&_.ProseMirror]:py-8 [&_.ProseMirror]:font-serif [&_.ProseMirror]:text-base [&_.ProseMirror]:leading-7"
                          />
                        </div>
                        <div className="flex flex-col sm:flex-row justify-end gap-2">
                          <Button
                            variant="outline"
                            onClick={() => {
                              setShowLetterPreview(false);
                              setGeneratedLetter('');
                              setEditedLetterHtml('');
                              setLetterSaved(false);
                              setApplicationDraftId(null);
                            }}
                            className="text-red-600 border-red-300 hover:bg-red-50 w-full sm:w-auto"
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            Discard
                          </Button>
                          <Button
                            onClick={async () => {
                              setSavingLetter(true);
                              try {
                                const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                                const response = await authenticatedFetch(`${backendUrl}/api/teacher/save-application-draft`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  credentials: 'include',
                                  body: JSON.stringify({
                                    html_content: editedLetterHtml,
                                    application_type: applicationTypes.find(t => t.value === applicationType)?.label || applicationType,
                                    plain_text: applicationPlainText,
                                    formatted_letter: generatedLetter,
                                  })
                                });
                                if (response.ok) {
                                  const data = await response.json();
                                  setApplicationDraftId(data.draft_id);
                                  setLetterSaved(true);
                                  toast.success('Application letter saved and PDF generated!');
                                } else {
                                  const err = await response.json();
                                  throw new Error(err.detail || 'Failed to save draft');
                                }
                              } catch (error) {
                                console.error('Error saving draft:', error);
                                toast.error(error.message || 'Failed to save letter');
                              } finally {
                                setSavingLetter(false);
                              }
                            }}
                            disabled={savingLetter}
                            className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
                            data-testid="save-letter-btn"
                          >
                            {savingLetter ? (
                              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
                            ) : (
                              <><CheckCircle className="h-4 w-4 mr-2" />Save Letter</>
                            )}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* After Save: Show formatted preview + edit option */
                      <div className="space-y-3">
                        <div className="bg-white border-2 border-gray-200 rounded-lg p-4 sm:p-6 shadow-inner max-h-[400px] overflow-y-auto mx-auto" style={{ maxWidth: '210mm' }}>
                          <div className="font-['Georgia',_'Times_New_Roman',_serif] text-base leading-7 text-gray-900" dangerouslySetInnerHTML={{ __html: editedLetterHtml }} />
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setLetterSaved(false)}
                            className="w-full sm:w-auto"
                          >
                            <FileEdit className="h-4 w-4 mr-1" />
                            Edit Again
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={printLetter}
                            className="text-gray-600 w-full sm:w-auto"
                          >
                            <Printer className="h-4 w-4 mr-1" />
                            Print/PDF
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Attachments Section - visible after letter is saved */}
                    {letterSaved && (
                      <div className="border-t pt-4 mt-4">
                        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                          <h4 className="font-medium text-gray-700 flex items-center gap-2">
                            <Paperclip className="h-4 w-4" />
                            Supporting Documents
                            {applicationAttachments.length > 0 && (
                              <Badge className="bg-blue-100 text-blue-700">{applicationAttachments.length}</Badge>
                            )}
                          </h4>
                          <div>
                            <input
                              type="file"
                              ref={attachmentInputRef}
                              onChange={handleAttachmentUpload}
                              multiple
                              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
                              className="hidden"
                              id="attachment-input"
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => attachmentInputRef.current?.click()}
                              disabled={uploadingAttachment}
                              className="text-blue-600 border-blue-300 hover:bg-blue-50"
                            >
                              {uploadingAttachment ? (
                                <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Uploading...</>
                              ) : (
                                <><Plus className="h-4 w-4 mr-1" />Add Document</>
                              )}
                            </Button>
                          </div>
                        </div>
                        
                        {applicationAttachments.length === 0 ? (
                          <p className="text-sm text-gray-500 italic">No attachments yet. Add supporting documents if needed.</p>
                        ) : (
                          <div className="space-y-2">
                            {applicationAttachments.map((att) => (
                              <div key={att.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border">
                                <div className="flex items-center gap-2 min-w-0">
                                  <FileIcon className="h-4 w-4 text-blue-500 shrink-0" />
                                  <span className="text-sm font-medium truncate">{att.filename}</span>
                                  <span className="text-xs text-gray-400 shrink-0">{formatFileSize(att.file_size)}</span>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeAttachment(att.id)}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0 shrink-0"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                        <p className="text-xs text-gray-400 mt-2">Allowed: PDF, Images (PNG, JPEG), Word, Excel. Max 5MB each.</p>
                      </div>
                    )}

                    {/* Submit Button - only after letter is saved */}
                    {letterSaved && (
                      <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2">
                          <Button
                            variant="outline"
                            onClick={() => {
                              setShowLetterPreview(false);
                              setGeneratedLetter('');
                              setEditedLetterHtml('');
                              setLetterSaved(false);
                              setApplicationDraftId(null);
                            }}
                            className="text-red-600 border-red-300 hover:bg-red-50 w-full sm:w-auto"
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            Discard
                          </Button>
                          <Button
                            onClick={submitApplication}
                            disabled={submittingApplication}
                            className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
                            data-testid="submit-application-btn"
                          >
                          {submittingApplication ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting...</>
                          ) : (
                            <><Send className="h-4 w-4 mr-2" />Submit Application</>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                </div>
                )}

                {/* Submitted Applications Sub-Tab */}
                {applicationSubTab === 'submitted' && (
                  <div className="space-y-4">
                    {myApplications.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                        <p className="text-lg font-medium">No applications submitted yet</p>
                        <p className="text-sm mt-2">Create your first application to get started</p>
                        <Button
                          onClick={() => setApplicationSubTab('create')}
                          className="mt-4"
                          variant="outline"
                        >
                          <FileEdit className="h-4 w-4 mr-2" />
                          Create Application
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-sm text-gray-500 mb-4">
                          Click on any application to view full details
                        </p>
                        {myApplications.map((app) => (
                          <div 
                            key={app.id} 
                            onClick={() => {
                              setSelectedApplication(app);
                              setShowApplicationModal(true);
                            }}
                            className={`p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                              app.status === 'approved' ? 'bg-green-50 border-green-200 hover:border-green-400' :
                              app.status === 'disapproved' ? 'bg-red-50 border-red-200 hover:border-red-400' :
                              app.status === 'escalated_to_siso' ? 'bg-purple-50 border-purple-200 hover:border-purple-400' :
                              app.status === 'SISO_APPROVED' ? 'bg-emerald-50 border-emerald-200 hover:border-emerald-400' :
                              app.status === 'SISO_DISAPPROVED' ? 'bg-orange-50 border-orange-200 hover:border-orange-400' :
                              'bg-gray-50 border-gray-200 hover:border-gray-400'
                            }`}
                            data-testid={`application-item-${app.id}`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <p className="font-medium text-gray-900">
                                {app.application_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </p>
                              <Badge 
                                className={
                                  app.status === 'approved' ? 'bg-green-100 text-green-700' :
                                  app.status === 'disapproved' ? 'bg-red-100 text-red-700' :
                                  app.status === 'escalated_to_siso' ? 'bg-purple-100 text-purple-700' :
                                  app.status === 'SISO_APPROVED' ? 'bg-emerald-100 text-emerald-700' :
                                  app.status === 'SISO_DISAPPROVED' ? 'bg-orange-100 text-orange-700' :
                                  'bg-yellow-100 text-yellow-700'
                                }
                              >
                                {app.status === 'approved' ? (
                                  <><CheckCircle className="h-3 w-3 mr-1" />Approved</>
                                ) : app.status === 'disapproved' ? (
                                  <><AlertCircle className="h-3 w-3 mr-1" />Disapproved</>
                                ) : app.status === 'escalated_to_siso' ? (
                                  <><Send className="h-3 w-3 mr-1" />Escalated to SISO</>
                                ) : app.status === 'SISO_APPROVED' ? (
                                  <><CheckCircle className="h-3 w-3 mr-1" />SISO Approved</>
                                ) : app.status === 'SISO_DISAPPROVED' ? (
                                  <><AlertCircle className="h-3 w-3 mr-1" />SISO Disapproved</>
                                ) : (
                                  <><Clock className="h-3 w-3 mr-1" />Pending</>
                                )}
                              </Badge>
                            </div>
                            <div className="flex items-center justify-between text-sm text-gray-500">
                              <span>Submitted: {new Date(app.submitted_at).toLocaleDateString()}</span>
                              <span className="text-blue-600 flex items-center">
                                View Details <ChevronRight className="h-4 w-4 ml-1" />
                              </span>
                            </div>
                            
                            {/* Quick Status Info */}
                            {app.status !== 'pending' && app.action_at && (
                              <div className={`mt-2 p-2 rounded text-xs ${
                                app.status === 'approved' || app.status === 'SISO_APPROVED' ? 'bg-green-100' :
                                app.status === 'disapproved' || app.status === 'SISO_DISAPPROVED' ? 'bg-red-100' :
                                'bg-purple-100'
                              }`}>
                                <p className="font-medium">
                                  {app.status === 'approved' && '✓ Approved by Headteacher'}
                                  {app.status === 'disapproved' && '✗ Disapproved by Headteacher'}
                                  {app.status === 'escalated_to_siso' && '↗ Escalated to SISO'}
                                  {app.status === 'SISO_APPROVED' && '✓ Approved by SISO'}
                                  {app.status === 'SISO_DISAPPROVED' && '✗ Disapproved by SISO'}
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Application View Modal */}
            {showApplicationModal && selectedApplication && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
                  {/* Modal Header */}
                  <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold">
                        {selectedApplication.application_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </h2>
                      <p className="text-sm text-blue-100">
                        Submitted on {new Date(selectedApplication.submitted_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowApplicationModal(false);
                        setSelectedApplication(null);
                      }}
                      className="text-white hover:bg-white/20"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>

                  {/* Modal Content */}
                  <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                    {/* Status Badge */}
                    <div className="flex items-center gap-3 mb-6">
                      <span className="text-sm font-medium text-gray-600">Status:</span>
                      <Badge 
                        className={`text-sm px-3 py-1 ${
                          selectedApplication.status === 'approved' ? 'bg-green-100 text-green-700' :
                          selectedApplication.status === 'disapproved' ? 'bg-red-100 text-red-700' :
                          selectedApplication.status === 'escalated_to_siso' ? 'bg-purple-100 text-purple-700' :
                          selectedApplication.status === 'SISO_APPROVED' ? 'bg-emerald-100 text-emerald-700' :
                          selectedApplication.status === 'SISO_DISAPPROVED' ? 'bg-orange-100 text-orange-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        {selectedApplication.status === 'approved' ? 'Approved by Headteacher' :
                         selectedApplication.status === 'disapproved' ? 'Disapproved by Headteacher' :
                         selectedApplication.status === 'escalated_to_siso' ? 'Escalated to SISO' :
                         selectedApplication.status === 'SISO_APPROVED' ? 'Approved by SISO' :
                         selectedApplication.status === 'SISO_DISAPPROVED' ? 'Disapproved by SISO' :
                         'Pending Review'}
                      </Badge>
                    </div>

                    {/* Application Letter Content */}
                    <div className="border rounded-lg p-6 bg-gray-50 mb-6">
                      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <FileText className="h-5 w-5 text-blue-600" />
                        Application Letter
                      </h3>
                      <div className="bg-white border rounded-lg p-6 shadow-inner">
                        <div className="font-['Georgia',_'Times_New_Roman',_serif] text-base leading-7 text-gray-900">
                          {selectedApplication.formatted_letter ? 
                            renderFormattedLetter(selectedApplication.formatted_letter, userProfile?.digital_signature, null) :
                            <pre className="whitespace-pre-wrap font-serif">{selectedApplication.plain_text || 'No content available'}</pre>
                          }
                        </div>
                      </div>
                    </div>

                    {/* Action History */}
                    {selectedApplication.action_at && (
                      <div className={`border rounded-lg p-4 ${
                        selectedApplication.status === 'approved' || selectedApplication.status === 'SISO_APPROVED' ? 'bg-green-50 border-green-200' :
                        selectedApplication.status === 'disapproved' || selectedApplication.status === 'SISO_DISAPPROVED' ? 'bg-red-50 border-red-200' :
                        'bg-purple-50 border-purple-200'
                      }`}>
                        <h4 className="font-semibold text-gray-900 mb-2">Action Taken</h4>
                        <div className="text-sm space-y-1">
                          <p>
                            <span className="font-medium">Action:</span>{' '}
                            {selectedApplication.status === 'approved' && 'Approved by Headteacher'}
                            {selectedApplication.status === 'disapproved' && 'Disapproved by Headteacher'}
                            {selectedApplication.status === 'escalated_to_siso' && 'Escalated to SISO by Headteacher'}
                            {selectedApplication.status === 'SISO_APPROVED' && 'Approved by SISO'}
                            {selectedApplication.status === 'SISO_DISAPPROVED' && 'Disapproved by SISO'}
                          </p>
                          <p>
                            <span className="font-medium">Date:</span>{' '}
                            {new Date(selectedApplication.action_at).toLocaleString()}
                          </p>
                          {selectedApplication.action_comment && (
                            <p>
                              <span className="font-medium">Comment:</span>{' '}
                              {selectedApplication.action_comment}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Modal Footer */}
                  <div className="border-t p-4 bg-gray-50 flex justify-end gap-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowApplicationModal(false);
                        setSelectedApplication(null);
                      }}
                    >
                      Close
                    </Button>
                    <Button
                      onClick={printSelectedApplication}
                      className="bg-blue-600 hover:bg-blue-700"
                      disabled={!selectedApplication.formatted_letter}
                    >
                      <Printer className="h-4 w-4 mr-2" />
                      Print/Download PDF
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  My Profile & Settings
                </CardTitle>
                <CardDescription>
                  Manage your profile picture, digital signature, and contact details
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {profileLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  </div>
                ) : (
                  <>
                    {/* Upload Sections - Inline on Desktop */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Profile Picture */}
                      <div className="border rounded-lg p-4">
                        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                          <Users className="h-4 w-4 text-blue-600" />
                          Profile Picture
                        </h3>
                        <div className="flex flex-col items-center gap-3">
                          {userProfile?.profile_picture ? (
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${userProfile.profile_picture}`} alt="Profile" className="w-20 h-20 rounded-full object-cover border-2 border-blue-200" data-testid="profile-picture-preview" />
                          ) : (
                            <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center border-2 border-gray-300">
                              <Users className="h-8 w-8 text-gray-400" />
                            </div>
                          )}
                          <div className="flex flex-col gap-2 w-full">
                            <label className="cursor-pointer w-full">
                              <input type="file" accept="image/*" className="hidden" onChange={handleProfilePictureUpload} disabled={uploadingProfilePic} data-testid="profile-picture-input" />
                              <Button variant="outline" size="sm" disabled={uploadingProfilePic} className="w-full" asChild>
                                <span>{uploadingProfilePic ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Uploading</> : <><Upload className="h-3 w-3 mr-1" />Upload</>}</span>
                              </Button>
                            </label>
                            {userProfile?.profile_picture && (
                              <Button variant="outline" size="sm" className="text-red-600 border-red-300 hover:bg-red-50 w-full" onClick={() => handleDeleteUpload('profile_picture')} data-testid="delete-profile-pic-btn">
                                <Trash2 className="h-3 w-3 mr-1" />Remove
                              </Button>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2 text-center">Visible to headteacher & SISO</p>
                      </div>

                      {/* Digital Signature */}
                      <div className="border rounded-lg p-4">
                        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                          <FileEdit className="h-4 w-4 text-green-600" />
                          Digital Signature
                        </h3>
                        <div className="flex flex-col items-center gap-3">
                          {userProfile?.digital_signature ? (
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${userProfile.digital_signature}`} alt="Signature" className="w-28 h-14 object-contain border rounded bg-white p-1" data-testid="signature-preview" />
                          ) : (
                            <div className="w-28 h-14 bg-gray-100 flex items-center justify-center border rounded">
                              <span className="text-xs text-gray-400">No signature</span>
                            </div>
                          )}
                          <div className="flex flex-col gap-2 w-full">
                            <label className="cursor-pointer w-full">
                              <input type="file" accept="image/*" className="hidden" onChange={handleSignatureUpload} disabled={uploadingSignature} data-testid="signature-input" />
                              <Button variant="outline" size="sm" disabled={uploadingSignature} className="w-full" asChild>
                                <span>{uploadingSignature ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Uploading</> : <><Upload className="h-3 w-3 mr-1" />Upload</>}</span>
                              </Button>
                            </label>
                            {userProfile?.digital_signature && (
                              <Button variant="outline" size="sm" className="text-red-600 border-red-300 hover:bg-red-50 w-full" onClick={() => handleDeleteUpload('signature')} data-testid="delete-signature-btn">
                                <Trash2 className="h-3 w-3 mr-1" />Remove
                              </Button>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2 text-center">For application letters</p>
                      </div>
                    </div>

                    {/* Contact Details Section */}
                    <div className="border rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <BookOpen className="h-5 w-5 text-purple-600" />
                        Contact Details
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Town/City</label>
                          <input type="text" value={profileFormData.town} onChange={(e) => setProfileFormData(prev => ({ ...prev, town: e.target.value }))} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter your town" data-testid="profile-town-input" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                          <input type="tel" value={profileFormData.phone} onChange={(e) => setProfileFormData(prev => ({ ...prev, phone: e.target.value }))} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g., 0244123456" data-testid="profile-phone-input" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">District File Number</label>
                          <input type="text" value={profileFormData.file_number} onChange={(e) => setProfileFormData(prev => ({ ...prev, file_number: e.target.value }))} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter your district file number" data-testid="profile-file-number-input" />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">School Name</label>
                          <input type="text" value={profileFormData.school_name || ''} onChange={(e) => setProfileFormData(prev => ({ ...prev, school_name: e.target.value }))} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter your school name" data-testid="profile-school-name-input" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">District Name</label>
                          <input type="text" value={profileFormData.district_name || ''} onChange={(e) => setProfileFormData(prev => ({ ...prev, district_name: e.target.value }))} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter your district name" data-testid="profile-district-name-input" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Region Name</label>
                          <input type="text" value={profileFormData.region_name || ''} onChange={(e) => setProfileFormData(prev => ({ ...prev, region_name: e.target.value }))} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter your region name" data-testid="profile-region-name-input" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Regional File Number</label>
                          <input type="text" value={profileFormData.regional_file_number || ''} onChange={(e) => setProfileFormData(prev => ({ ...prev, regional_file_number: e.target.value }))} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter your regional file number" data-testid="profile-regional-file-input" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Staff ID</label>
                          <input type="text" value={profileFormData.staff_id || ''} onChange={(e) => setProfileFormData(prev => ({ ...prev, staff_id: e.target.value }))} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter your GES Staff ID" data-testid="profile-staff-id-input" />
                        </div>
                      </div>
                      
                      {/* Referral Link Section */}
                      <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <h4 className="font-medium text-purple-800 mb-3 flex items-center gap-2">
                          <UserPlus className="h-4 w-4" />
                          Your Referral Link
                        </h4>
                        <p className="text-xs text-purple-700 mb-3 font-semibold">
                          Invite 5 friend teachers and access lesson notes for free next term
                        </p>
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={referralLink}
                            readOnly
                            className="flex-1 text-sm px-3 py-2 bg-white border border-purple-200 rounded-md text-gray-700"
                            data-testid="profile-referral-link"
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={copyReferralLink}
                            className="border-purple-300 text-purple-700 hover:bg-purple-100"
                            data-testid="copy-referral-btn"
                          >
                            <Copy className="h-4 w-4 mr-1" />
                            Copy
                          </Button>
                        </div>
                        <p className="text-xs text-purple-600 mt-2">
                          Share this link with fellow teachers. When 5 teachers sign up using your link, you'll get free access next term!
                        </p>
                      </div>
                      
                      <div className="mt-4 flex justify-end">
                        <Button onClick={handleSaveProfile} disabled={savingProfile} className="bg-blue-600 hover:bg-blue-700" data-testid="save-profile-btn">
                          {savingProfile ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : <><CheckCircle className="h-4 w-4 mr-2" />Save Details</>}
                        </Button>
                      </div>
                    </div>

                    {/* Account Info */}
                    {userProfile && (
                      <div className="border rounded-lg p-4 bg-gray-50">
                        <h3 className="font-semibold text-gray-900 mb-3">Account Information</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                          <div><span className="text-gray-500">Name:</span></div>
                          <div className="font-medium">{userProfile.name}</div>
                          <div><span className="text-gray-500">Email:</span></div>
                          <div className="font-medium">{userProfile.email}</div>
                          <div><span className="text-gray-500">Role:</span></div>
                          <div className="font-medium capitalize">{userProfile.role}</div>
                          {userProfile.school && (
                            <>
                              <div><span className="text-gray-500">School:</span></div>
                              <div className="font-medium">{userProfile.school}</div>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Promotion Exams Preparation Tab */}
          <TabsContent value="promotion_exams" forceMount className={mainTab !== 'promotion_exams' ? 'hidden' : ''}>
            <PromotionPreparation />
          </TabsContent>
        </Tabs>
      </div>

      {/* Lesson Delivery Status Modal - At Root Level */}
      {showDeliveryModal && selectedDeliveryNote && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeDeliveryModal}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4 rounded-t-xl">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <FileEdit className="h-5 w-5" />
                Lesson Delivery Status
              </h2>
              <p className="text-sm text-purple-100 mt-1">
                {selectedDeliveryNote.subject} - Week {selectedDeliveryNote.week}
              </p>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              {/* Lesson Info */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-600">
                  <strong>Class:</strong> {selectedDeliveryNote.class_name || selectedDeliveryNote.class_level}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Submitted:</strong> {selectedDeliveryNote.submitted_at ? new Date(selectedDeliveryNote.submitted_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>

              {/* Delivery Status Selection */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Was this lesson delivered in class?
                </label>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant={deliveryStatus === 'delivered' ? 'default' : 'outline'}
                    className={`flex-1 ${deliveryStatus === 'delivered' ? 'bg-emerald-600 hover:bg-emerald-700' : 'border-emerald-500 text-emerald-600 hover:bg-emerald-50'}`}
                    onClick={() => setDeliveryStatus('delivered')}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Yes, Delivered
                  </Button>
                  <Button
                    type="button"
                    variant={deliveryStatus === 'not_delivered' ? 'default' : 'outline'}
                    className={`flex-1 ${deliveryStatus === 'not_delivered' ? 'bg-red-600 hover:bg-red-700' : 'border-red-500 text-red-600 hover:bg-red-50'}`}
                    onClick={() => setDeliveryStatus('not_delivered')}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Not Delivered
                  </Button>
                </div>
              </div>

              {/* Remarks Section (shown when not delivered) */}
              {deliveryStatus === 'not_delivered' && (
                <div className="space-y-4 animate-in slide-in-from-top-2 duration-200">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Reason for Not Delivering <span className="text-red-500">*</span>
                    </label>
                    <Textarea
                      value={deliveryRemarks}
                      onChange={(e) => setDeliveryRemarks(e.target.value)}
                      placeholder="e.g., School closed due to bad weather, Personal emergency, Lack of teaching materials..."
                      className="min-h-[80px] resize-none"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Action Plan to Deliver
                    </label>
                    <Textarea
                      value={deliveryActionPlan}
                      onChange={(e) => setDeliveryActionPlan(e.target.value)}
                      placeholder="e.g., Will deliver next Monday, Arranged make-up class for Friday..."
                      className="min-h-[60px] resize-none"
                    />
                  </div>
                </div>
              )}

              {/* Success message for delivered */}
              {deliveryStatus === 'delivered' && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 animate-in slide-in-from-top-2 duration-200">
                  <p className="text-sm text-emerald-700 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Great! This lesson was successfully delivered to students.
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="border-t p-4 bg-gray-50 rounded-b-xl flex justify-end gap-3">
              <Button variant="outline" onClick={closeDeliveryModal}>
                Cancel
              </Button>
              <Button
                onClick={updateDeliveryStatus}
                disabled={!deliveryStatus || savingDeliveryStatus || (deliveryStatus === 'not_delivered' && !deliveryRemarks.trim())}
                className={deliveryStatus === 'delivered' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-purple-600 hover:bg-purple-700'}
              >
                {savingDeliveryStatus ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Save Status
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Respond to Comment Modal - At Root Level */}
      {showRespondModal && selectedCommentNote && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeRespondModal}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 rounded-t-xl">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Respond to Headteacher
              </h2>
              <p className="text-sm text-blue-100 mt-1">
                {selectedCommentNote.subject} - Week {selectedCommentNote.week}
              </p>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              {/* Original Comment */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs font-medium text-amber-800 mb-1">Headteacher's Comment:</p>
                <p className="text-sm text-amber-900">{selectedCommentNote.headteacher_comment}</p>
                {selectedCommentNote.comment_date && (
                  <p className="text-[10px] text-amber-600 mt-2">
                    {new Date(selectedCommentNote.comment_date).toLocaleDateString()}
                  </p>
                )}
              </div>

              {/* Response Input */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Your Response <span className="text-red-500">*</span>
                </label>
                <Textarea
                  value={responseNote}
                  onChange={(e) => setResponseNote(e.target.value)}
                  placeholder="Explain how you've addressed the comment or what changes you've made..."
                  className="min-h-[120px] resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Your headteacher will be notified of your response.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t p-4 bg-gray-50 rounded-b-xl flex justify-end gap-3">
              <Button variant="outline" onClick={closeRespondModal}>
                Cancel
              </Button>
              <Button
                onClick={submitResponse}
                disabled={!responseNote.trim() || submittingResponse}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {submittingResponse ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Response
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Lesson Note Editor Modal */}
      {/* View Note Modal */}
      {viewingNote && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4" data-testid="note-view-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col">
            <div className="bg-gradient-to-r from-gray-700 to-gray-800 text-white p-4 rounded-t-xl flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  View Lesson Note
                </h2>
                <p className="text-sm text-gray-300 mt-0.5">{viewingNote.title || `${viewingNote.subject} - ${viewingNote.class_name}`}</p>
              </div>
              <Button variant="ghost" size="sm" className="text-white hover:bg-white/20" onClick={() => { setViewingNote(null); setViewingNoteContent(''); }}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              <div className="flex gap-2 mb-4 flex-wrap">
                <Badge variant="outline">{viewingNote.class_name}</Badge>
                <Badge variant="outline">{viewingNote.subject}</Badge>
                <Badge variant="outline">Week {viewingNote.week}</Badge>
              </div>
              <div
                className="prose prose-sm max-w-none text-gray-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: viewingNoteContent }}
              />
            </div>
            <div className="p-3 border-t flex justify-end bg-gray-50 rounded-b-xl">
              <Button variant="outline" onClick={() => { setViewingNote(null); setViewingNoteContent(''); }}>Close</Button>
            </div>
          </div>
        </div>
      )}

      {editingNote && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4" data-testid="note-editor-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col">
            <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white p-4 rounded-t-xl flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <FileEdit className="h-5 w-5" />
                  Edit Lesson Note
                </h2>
                <p className="text-sm text-amber-100 mt-0.5">{editingNote.title}</p>
              </div>
              <Button variant="ghost" size="sm" className="text-white hover:bg-white/20" onClick={() => { setEditingNote(null); setEditNoteContent(''); }}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="p-3 text-xs text-gray-500 bg-amber-50 border-b flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-amber-500" />
              Use the editor below to customize this lesson note. Click &ldquo;Save Changes&rdquo; when finished.
            </div>
            <div className="flex-1 overflow-y-auto p-4" data-testid="note-editor-content">
              <RichTextEditor
                content={editNoteContent}
                onChange={setEditNoteContent}
              />
            </div>
            <div className="p-3 border-t flex flex-col sm:flex-row items-stretch sm:items-center gap-2 bg-gray-50 rounded-b-xl">
              <Button variant="outline" onClick={() => { setEditingNote(null); setEditNoteContent(''); }} data-testid="cancel-note-edit-btn" className="w-full sm:w-auto">Cancel</Button>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto sm:ml-auto">
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                      // Save first, then download the personalized PDF
                      await saveNoteEdit();
                      const noteId = editingNote.id;
                      const response = await authenticatedFetch(`${backendUrl}/api/teacher/lesson-note-download/${noteId}`);
                      if (response.ok) {
                        const blob = await response.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `${editingNote.title || editingNote.subject || 'lesson_note'}.pdf`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                        toast.success('Lesson note downloaded as PDF');
                      } else {
                        const err = await response.json().catch(() => ({}));
                        toast.error(err.detail || 'Download failed — check subscription');
                      }
                    } catch (e) { toast.error('Download failed'); }
                  }}
                  className="border-green-600 text-green-600 hover:bg-green-50 w-full sm:w-auto"
                  data-testid="download-note-from-editor-btn"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { submitToHeadteacher({ ...editingNote, lesson_content: editNoteContent }); }}
                  className="border-blue-600 text-blue-600 w-full sm:w-auto"
                >
                  <Upload className="h-4 w-4 mr-1" />
                  Save &amp; Upload to HT
                </Button>
                <Button
                  onClick={saveNoteEdit}
                  disabled={savingNoteEdit}
                  className="bg-amber-600 hover:bg-amber-700 w-full sm:w-auto"
                  data-testid="save-note-edit-btn"
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  {savingNoteEdit ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default TeacherDashboard;